import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/apiService";
import config from '../config';
import io from 'socket.io-client';
import useScoreboardAppearance from '../hooks/useScoreboardAppearance';
import Snooker from './Scoreboards/Snooker';
import Pool from './Scoreboards/Pool';
import UsPool from './Scoreboards/UsPool';
import ChinesePool from './Scoreboards/ChinesePool';
import RefereeControls from './RefereeControls';
import RefereeShootout from './RefereeShootout';
import { ShootoutGlobalTimer, ShootoutShotTimer } from './ShootoutDisplays';
import ShootoutInfoBoard from './ShootoutInfoBoard';
import { classifyRemoteFrameUpdate } from '../utils/frameEventSync';
import { resolveFrameStartTimestamp } from '../utils/frameTimeline';
import { SNOOKER_COLORS, adjustSnookerReds, snookerRedsSnapshot, applySnookerFoul, freeBallTarget, minimumFoulPoints, potSnookerBall, snookerPointsRemaining, snookerScoringSnapshot } from '../utils/snookerScoring';
import '../assets/css/Scoreboard.css';
import { statsFrameKey, STATS_DURATION_MS } from '../utils/streamingStats';

// Frame event history sent with every finished snooker frame. The backend uses
// it for qualification analysis; older clients that send nothing keep working,
// and remain pending until AI can qualify them.
// Bump FRAME_EVENT_SCHEMA_VERSION together with the backend schema version.
const FRAME_EVENT_SCHEMA_VERSION = 2;
const MAX_FRAME_EVENTS = 1500;

const createClientFrameId = () => {
    let entropy = Math.floor(Math.random() * 0x100000000).toString(36).padStart(7, '0');
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
        const values = new Uint32Array(1);
        window.crypto.getRandomValues(values);
        entropy = values[0].toString(36).padStart(7, '0');
    }
    return `web-${Date.now().toString(36)}-${entropy}`;
};

const cloneGameState = (state) => ({
    ...state,
    player1PottedBalls: [...(state.player1PottedBalls || [])],
    player2PottedBalls: [...(state.player2PottedBalls || [])],
    currentBreakPottedBalls: [...(state.currentBreakPottedBalls || [])],
    colorsRemaining: state.colorsRemaining ? { ...state.colorsRemaining } : state.colorsRemaining,
    colorsPottedFirstTime: state.colorsPottedFirstTime ? { ...state.colorsPottedFirstTime } : state.colorsPottedFirstTime,
});

const SNookerInitialState = Object.freeze({
    redsRemaining: 15,
    colorsRemaining: Object.freeze({ Yellow: 2, Green: 3, Brown: 4, Blue: 5, Pink: 6, Black: 7 }),
    colorsPottedFirstTime: Object.freeze({ Yellow: false, Green: false, Brown: false, Blue: false, Pink: false, Black: false }),
    player1Score: 0,
    player2Score: 0,
    player1Break: 0,
    player2Break: 0,
    currentPlayer: 1,
    currentBreakPoints: 0,
    currentBreakPottedBalls: Object.freeze([]),
    maxPoints: 147,
    player1PottedBalls: Object.freeze([]),
    player2PottedBalls: Object.freeze([]),
    player1FramesWon: 0,
    player2FramesWon: 0,
    currentFrame: 1,
    freeBallActive: false,
    streamingStatsCommand: null,
    selectingFoulValue: false,
    frameOver: false,
    lastRedPotted: false,
    firstColorAfterLastRedPotted: false,
    finalColorsPhase: false,
    firstPlayer: "Player A",
    secondPlayer: "Player B",
    firstPlayerNationality: "MA",
    secondPlayerNationality: "MA",
    timerStartTimestamp: null,
    timerPausedDuration: 0,
    timerIsRunning: false,
    sequence: 0,
    timestamp: Date.now(),
    shotRemaining: 15000,
    shotTimerIsRunning: false,
    shotStartTimestamp: null,
    frameExpired: false,
    currentShotLimitMs: 15000,
    frameStartTimestamp: null,
    switchCount: 0,
    timerOwner: 1,
    timerSyncEnabled: true,
    timerSoundEnabled: false,
});

const PoolInitialState = Object.freeze({
    player1FramesWon: 0,
    player2FramesWon: 0,
    currentFrame: 1,
    currentPlayer: 1,
    totalSeconds: 0,
    time: "00 : 00 : 00",
    firstPlayer: "Player A",
    secondPlayer: "Player B",
    firstPlayerNationality: "MA",
    secondPlayerNationality: "MA",
    sequence: 0,
    timestamp: Date.now(),
    isGameFinished: false,
    winMessage: "",
    shotTimeRemaining: 45,
    player1ExtensionUsed: false,
    player2ExtensionUsed: false,
});

const ChinesePoolInitialState = Object.freeze({
    player1FramesWon: 0,
    player2FramesWon: 0,
    currentFrame: 1,
    currentPlayer: 1,
    totalSeconds: 0,
    time: "00 : 00 : 00",
    firstPlayer: "Player A",
    secondPlayer: "Player B",
    firstPlayerNationality: "MA",
    secondPlayerNationality: "MA",
    sequence: 0,
    timestamp: Date.now(),
    isGameFinished: false,
    winMessage: "",
    shotTimeRemaining: 45,
    player1ExtensionUsed: false,
    player2ExtensionUsed: false,
    player1IllegalBreaks: 0,
    player2IllegalBreaks: 0,
});

const InitialSettings = Object.freeze({
    pool: Object.freeze({
        pool_shot_timer_enabled: "true",
        pool_shot_time: "45",
        pool_extension_time: "15",
    }),
    'us-pool': Object.freeze({
        us_pool_shot_timer_enabled: "true",
        us_pool_shot_time: "45",
        us_pool_extension_time: "15",
    }),
    'chinese-pool': Object.freeze({
        chinese_pool_shot_timer_enabled: "true",
        chinese_pool_shot_time: "45",
        chinese_pool_extension_time: "15",
    }),
    snooker: Object.freeze({
        snooker_shot_timer_enabled: "false",
    }),
});

const getSettingsKeys = (type) => {
    switch (type) {
        case "pool":
            return ['pool_shot_timer_enabled', 'pool_shot_time', 'pool_extension_time'];
        case "us-pool":
            return ['us_pool_shot_timer_enabled', 'us_pool_shot_time', 'us_pool_extension_time'];
        case "chinese-pool":
            return ['chinese_pool_shot_timer_enabled', 'chinese_pool_shot_time', 'chinese_pool_extension_time'];
        case "snooker":
            return ['snooker_shot_timer_enabled'];
        default:
            return [];
    }
};

const shallowEqual = (objA, objB) => {
    if (objA === objB) return true;
    if (!objA || !objB) return false;
    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);
    if (keysA.length !== keysB.length) return false;
    for (let key of keysA) {
        if (objA[key] !== objB[key]) return false;
    }
    return true;
};

const GameWrapper = () => {
    const { type, id: linkId, view } = useParams();
    const { user, loading, isAuthenticated } = useAuth();

    const isPublic = ['referee', 'referee-shootout'].includes(view);

    const [gameState, setGameState] = useState(() => {
        switch (type) {
            case 'snooker': return { ...SNookerInitialState, timestamp: Date.now() };
            case 'pool': return { ...PoolInitialState, timestamp: Date.now() };
            case 'us-pool': return { ...PoolInitialState, timestamp: Date.now() };
            case 'chinese-pool': return { ...ChinesePoolInitialState, timestamp: Date.now() };
            default: return {};
        }
    });
    const [settings, setSettings] = useState(() => {
        return { ...InitialSettings[type] } || {};
    });
    const [displayTime, setDisplayTime] = useState('00:00:00');
    const [frameBreaks, setFrameBreaks] = useState([]);
    const [actionStack, setActionStack] = useState([]);
    const [game, setGame] = useState();
    const [isLinkedGame, setIsLinkedGame] = useState(false);
    const [isGameFinished, setIsGameFinished] = useState(false);
    const [players, setPlayers] = useState([]);
    const [socket, setSocket] = useState(null);
    const scoreboardTheme = useScoreboardAppearance(type === 'snooker' ? game?.link : null);
    const [showControls, setShowControls] = useState(type === 'snooker');
    const isUpdatingFromSocketRef = useRef(false);
    const [hasReceivedInitialData, setHasReceivedInitialData] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const [lastLocalUpdate, setLastLocalUpdate] = useState(0);
    const [frameTimer, setFrameTimer] = useState(null);
    const [shotTimer, setShotTimer] = useState(null);
    const [loadingSettings, setLoadingSettings] = useState(true);
    const [gameLoading, setGameLoading] = useState(true);
    const [gameOwnership, setGameOwnership] = useState(null);
    const [actionQueue, setActionQueue] = useState([]);
    const [isProcessingQueue, setIsProcessingQueue] = useState(false);
    const [shotTimerIsRunning, setShotTimerIsRunning] = useState(false);
    const [shotTimerOwner, setShotTimerOwner] = useState(null);
    const [winMessage, setWinMessage] = useState(null);

    const debounceTimeoutRef = useRef(null);
    const isLocalActionRef = useRef(false);
    const getCurrentGameStateRef = useRef(null);
    const syncStateFromSocketRef = useRef();
    const hasReceivedInitialDataRef = useRef(false);
    const isInitializingRef = useRef(true);
    const isWinningFrameRef = useRef(false);
    const lastEmittedSecondRef = useRef(null);
    const lastEmittedPayloadRef = useRef(null);
    const lastReceivedTimestampRef = useRef(0);
    const lastAcceptedSequenceRef = useRef(-1);
    const lastEmittedTimestampRef = useRef(0);
    const frameStorePromisesRef = useRef(new Map());
    const cancelledCompletionIdsRef = useRef(new Set());

    const actionFunctionsRef = useRef({});
    const refereeConfirmationRef = useRef(null);
    const lastKeyActionTimeRef = useRef(0);
    const getShotTimerEnabled = useCallback(() => {
        const prefix = type.replace('-', '_');
        return settings[`${prefix}_shot_timer_enabled`] === 'true';
    }, [type, settings]);
    const getShotTimeSetting = useCallback(() => {
        const prefix = type.replace('-', '_');
        return settings[`${prefix}_shot_time`] || '45';
    }, [type, settings]);
    const calculateCurrentTime = useCallback(() => {
        if (type === 'snooker') {
            if (!gameState.timerIsRunning || !gameState.timerStartTimestamp) {
                return gameState.timerPausedDuration;
            }
            const elapsedSinceStart = Date.now() - gameState.timerStartTimestamp;
            return gameState.timerPausedDuration + elapsedSinceStart;
        } else {
            return gameState.totalSeconds * 1000;
        }
    }, [gameState.timerIsRunning, gameState.timerStartTimestamp, gameState.timerPausedDuration, gameState.totalSeconds, type]);
    const calculateShotRemaining = useCallback(() => {
        if (!gameState.shotTimerIsRunning || !gameState.shotStartTimestamp) {
            return gameState.shotRemaining;
        }
        const elapsed = Date.now() - gameState.shotStartTimestamp;
        return Math.max(0, gameState.shotRemaining - elapsed);
    }, [gameState.shotTimerIsRunning, gameState.shotStartTimestamp, gameState.shotRemaining]);
    const formatTime = useCallback((milliseconds) => {
        const totalSeconds = Math.floor(milliseconds / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }, []);
    const formatShotTime = (milliseconds) => {
        const seconds = Math.ceil(milliseconds / 1000);
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    const memoizedMaxPoints = useMemo(() => {
        if (type !== 'snooker') return gameState.maxPoints;
        return snookerPointsRemaining(gameState);
    }, [type, gameState]);
    const getFlagComponent = (countryCode) => {
        const cc = (countryCode || '').slice(0, 2).toLowerCase();
        if (!cc) return null;
        const url = `https://flagcdn.com/${cc}.svg`;
        return (
            <span
                className="player-flag"
                role="img"
                aria-label={`flag ${cc}`}
                style={{ backgroundImage: `url(${url})` }}
            />
        );
    };
    const calculateDisplayTime = useCallback(() => {
        const elapsed = calculateCurrentTime();
        if (type === 'snooker' && getShotTimerEnabled()) {
            return Math.max(0, 600000 - elapsed);
        }
        return elapsed;
    }, [calculateCurrentTime, type, settings]);
    const updateDisplayTime = useCallback(() => {
        const currentTime = calculateDisplayTime();
        setDisplayTime(formatTime(currentTime));
    }, [calculateDisplayTime, formatTime]);
    const buildGameStatePayload = useCallback((state, gameId) => {
        const baseState = {
            id: gameId,
            first_player: state.firstPlayer,
            second_player: state.secondPlayer,
            current_player: state.currentPlayer,
            first_player_frames: state.player1FramesWon,
            second_player_frames: state.player2FramesWon,
            current_frame: state.currentFrame,
            sequence: state.sequence,
            timestamp: state.timestamp
        };
        if (type === 'snooker') {
            return {
                ...baseState,
                first_player_score: state.player1Score,
                second_player_score: state.player2Score,
                current_break_points: state.currentBreakPoints,
                current_break_potted_balls: state.currentBreakPottedBalls,
                reds_remaining: state.redsRemaining,
                colors_remaining: state.colorsRemaining,
                colors_potted_first_time: state.colorsPottedFirstTime,
                first_player_break: state.player1Break,
                second_player_break: state.player2Break,
                first_player_potted_balls: state.player1PottedBalls,
                second_player_potted_balls: state.player2PottedBalls,
                timer_start_timestamp: state.timerStartTimestamp,
                timer_paused_duration: state.timerPausedDuration,
                timer_is_running: state.timerIsRunning,
                free_ball_active: state.freeBallActive,
                streaming_stats_command: state.streamingStatsCommand,
                selecting_foul_value: state.selectingFoulValue,
                frame_over: state.frameOver,
                last_red_potted: state.lastRedPotted,
                first_color_after_last_red_potted: state.firstColorAfterLastRedPotted,
                final_colors_phase: state.finalColorsPhase,
                max_points: state.maxPoints,
                shot_remaining: state.shotRemaining,
                current_shot_limit_ms: state.currentShotLimitMs,
                shot_timer_is_running: state.shotTimerIsRunning,
                shot_start_timestamp: state.shotStartTimestamp,
                frame_expired: state.frameExpired,
                timer_owner: state.timerOwner,
                timer_sync_enabled: state.timerSyncEnabled,
                frame_start_timestamp: state.frameStartTimestamp,
                switch_count: state.switchCount,
            };
        } else {
            return {
                ...baseState,
                total_seconds: state.totalSeconds,
                shot_time_remaining: state.shotTimeRemaining,
                shot_timer_is_running: shotTimerIsRunning,
                player1_extension_used: state.player1ExtensionUsed,
                player2_extension_used: state.player2ExtensionUsed,
                is_game_finished: state.isGameFinished,
                win_message: state.winMessage,
                ...(type === 'chinese-pool' && {
                    player1_illegal_breaks: state.player1IllegalBreaks,
                    player2_illegal_breaks: state.player2IllegalBreaks
                })
            };
        }
    }, [type, shotTimerIsRunning]);
    const getCurrentGameState = useCallback(() => {
        return buildGameStatePayload(gameState, game?.id);
    }, [gameState, game?.id, type]);
    getCurrentGameStateRef.current = getCurrentGameState;
    const markLocalAction = () => {
        isLocalActionRef.current = true;
        setLastLocalUpdate(Date.now());
        setTimeout(() => {
            isLocalActionRef.current = false;
        }, 200);
    };
    const processActionQueue = useCallback(async () => {
        if (isProcessingQueue || actionQueue.length === 0) return;
        setIsProcessingQueue(true);
        const action = actionQueue[0];
        const newQueue = actionQueue.slice(1);
        setActionQueue(newQueue);
        await action.fn();
        markLocalAction();
        setIsProcessingQueue(false);
    }, [actionQueue, isProcessingQueue]);
    const enqueueAction = (fn) => {
        setActionQueue(prev => [...prev, { fn }]);
    };
    useEffect(() => {
        if (actionQueue.length > 0 && !isProcessingQueue) {
            processActionQueue();
        }
    }, [actionQueue, isProcessingQueue, processActionQueue]);
    const pushAction = (action) => {
        setActionStack(prev => [...prev, action]);
    };
    const frameEventsRef = useRef([]);
    const frameEventsTruncatedRef = useRef(false);
    // Events are appended from inside setGameState updaters, which React can
    // replay in development. The sequence number of the resulting state is
    // stable across those replays, so (sequence, type) is the dedupe key.
    const recordFrameEvent = useCallback((stateSequence, eventType, data = {}) => {
        if (type !== 'snooker') return;
        const events = frameEventsRef.current;
        for (let i = events.length - 1; i >= 0 && events[i].stateSequence === stateSequence; i--) {
            if (events[i].type === eventType) return;
        }
        if (events.length >= MAX_FRAME_EVENTS) {
            frameEventsTruncatedRef.current = true;
            return;
        }
        events.push({ stateSequence, type: eventType, timestamp_ms: Date.now(), ...data });
    }, [type]);
    const resetFrameEvents = useCallback(() => {
        frameEventsRef.current = [];
        frameEventsTruncatedRef.current = false;
    }, []);
    const snapshotFrameEvents = useCallback(() => ({
        events: JSON.parse(JSON.stringify(frameEventsRef.current)),
        truncated: frameEventsTruncatedRef.current,
    }), []);
    const restoreFrameEvents = useCallback((snapshot) => {
        frameEventsRef.current = Array.isArray(snapshot?.events)
            ? JSON.parse(JSON.stringify(snapshot.events))
            : [];
        frameEventsTruncatedRef.current = !!snapshot?.truncated;
    }, []);
    const buildFrameEventsPayload = useCallback((frameStartTimestamp) => {
        const events = frameEventsRef.current;
        if (!events.length) return null;
        const startedAt = resolveFrameStartTimestamp(events, frameStartTimestamp);
        return JSON.stringify({
            schema_version: FRAME_EVENT_SCHEMA_VERSION,
            truncated: frameEventsTruncatedRef.current,
            events: events.map(({ stateSequence, ...event }, index) => ({
                sequence: index + 1,
                elapsed_ms: Math.max(0, event.timestamp_ms - startedAt),
                ...event,
            })),
        });
    }, []);
    const checkGameOwnership = async () => {
        if (isPublic) {
            setGameLoading(false);
            return;
        }
        if (!user || !isAuthenticated()) return;
        try {
            const ownershipData = await apiService.post('/club/game/check-ownership', {
                link: linkId,
                user_id: user.id
            });
            setGameOwnership(ownershipData.belongs_to_user);
        } catch (error) {
            setGameOwnership(false);
        } finally {
            setGameLoading(false);
        }
    };
    const fetchAndApplySettings = async () => {
        setLoadingSettings(true);
        const keys = getSettingsKeys(type);
        if (keys.length === 0) {
            setLoadingSettings(false);
            return;
        }
        try {
            const endpoint = isPublic ? '/public/game-settings' : '/table/game-settings';
            const gameSettingsResponse = await apiService.post(endpoint, { table_id: game.id_table });
            if (gameSettingsResponse.success && JSON.parse(gameSettingsResponse.game_settings).use_game_settings === true) {
                setSettings(JSON.parse(gameSettingsResponse.game_settings));
                const shotTimeKey = `${type.replace('-', '_')}_shot_time`;
                if (JSON.parse(gameSettingsResponse.game_settings)[shotTimeKey]) {
                    setGameState(prevState => ({
                        ...prevState,
                        shotTimeRemaining: parseInt(JSON.parse(gameSettingsResponse.game_settings)[shotTimeKey]) || 45,
                    }));
                }
                setLoadingSettings(false);
                return;
            }
        } catch (err) {
            console.error('Error fetching game settings:', err);
        }
        try {
            const endpoint = isPublic ? `/public/settings/specific/${game.id_user || ''}` : `/settings/specific/${user?.id || ''}`;
            const response = await apiService.post(endpoint, { keys });
            if (response.success) {
                setSettings(response.settings);
                const shotTimeKey = `${type.replace('-', '_')}_shot_time`;
                if (response.settings[shotTimeKey]) {
                    setGameState(prevState => ({
                        ...prevState,
                        shotTimeRemaining: parseInt(response.settings[shotTimeKey]) || 45,
                    }));
                }
            }
        } catch (err) {
            setSettings({ ...InitialSettings[type] } || {});
            setGameState(prevState => ({
                ...prevState,
                shotTimeRemaining: 45,
            }));
        } finally {
            setLoadingSettings(false);
        }
    };
    const getGame = async () => {
        try {
            const endpoint = isPublic ? '/public/game-link' : '/club/game-link';
            const data = await apiService.post(endpoint, { link: linkId });
            if (data.is_linked_game === true) {
                setIsLinkedGame(true);
                setGame(data.linked_game);
            } else {
                setGame(data.game);
            }
            const playersEndpoint = isPublic ? '/public/players' : '/club/players';
            const playersPayload = isPublic ? { link: linkId } : { id: user.id };
            const playerData = await apiService.post(playersEndpoint, playersPayload);
            setPlayers(playerData.players);
        } catch (error) {
            console.error('Error fetching game:', error);
        }
    };
    const setGameWinner = async (winner) => {
        try {
            const endpoint = isPublic ? '/public/game/winner' : '/club/game/winner';
            await apiService.post(endpoint, {
                id: game.id,
                winner: winner
            });
        } catch (error) {
            console.error('Error setting game winner:', error);
        }
    };
    syncStateFromSocketRef.current = (incomingState) => {
        const isInitialStateLoad = isInitializing || !hasReceivedInitialData;

        if (!isInitialStateLoad) {
            if (isLocalActionRef.current &&
                incomingState.sequence <= (lastAcceptedSequenceRef.current || gameState.sequence)) {
                return;
            }
        }

        if (type !== 'snooker' && incomingState.shot_timer_command) {
            if (incomingState.shot_timer_command === 'stop') {
                if (shotTimer) {
                    clearInterval(shotTimer);
                    setShotTimer(null);
                }
                setShotTimerIsRunning(false);
                setShotTimerOwner(null);
            } else if (incomingState.shot_timer_command === 'start') {
                if (shotTimerOwner !== 'local' && !shotTimer) {
                    setShotTimerIsRunning(true);
                    setShotTimerOwner('remote');
                    const timer = setInterval(() => {
                        setGameState(prevState => {
                            if (prevState.shotTimeRemaining <= 1) {
                                clearInterval(timer);
                                setShotTimer(null);
                                setShotTimerIsRunning(false);
                                setShotTimerOwner(null);
                                enqueueAction(switchPlayer);
                                const newState = {
                                    ...prevState,
                                    shotTimeRemaining: parseInt(getShotTimeSetting()) || 45,
                                    sequence: prevState.sequence + 1,
                                    timestamp: Date.now()
                                };
                                emitGameState(newState, false);
                                return newState;
                            }
                            const newState = {
                                ...prevState,
                                shotTimeRemaining: prevState.shotTimeRemaining - 1,
                                sequence: prevState.sequence + 1,
                                timestamp: Date.now()
                            };
                            return newState;
                        });
                    }, 1000);
                    setShotTimer(timer);
                }
            }
        }

        const currentSeq = lastAcceptedSequenceRef.current;
        const shouldAccept = isInitialStateLoad || incomingState.sequence > currentSeq;

        if (shouldAccept) {
            lastReceivedTimestampRef.current = Date.now();
            lastAcceptedSequenceRef.current = incomingState.sequence;

            isUpdatingFromSocketRef.current = true;
            if (type !== 'snooker' && shotTimerOwner !== 'local') {
                if (incomingState.shot_timer_is_running !== undefined) {
                    setShotTimerIsRunning(incomingState.shot_timer_is_running);
                }
            }
            setGameState(prevState => {
                const currentPayload = buildGameStatePayload(prevState, game.id);
                if (shallowEqual(incomingState, currentPayload)) {
                    isUpdatingFromSocketRef.current = false;
                    return prevState;
                }
                const baseUpdate = {
                    currentPlayer: incomingState.current_player !== undefined ? incomingState.current_player : prevState.currentPlayer,
                    player1FramesWon: incomingState.first_player_frames !== undefined ? incomingState.first_player_frames : prevState.player1FramesWon,
                    player2FramesWon: incomingState.second_player_frames !== undefined ? incomingState.second_player_frames : prevState.player2FramesWon,
                    currentFrame: incomingState.current_frame !== undefined ? incomingState.current_frame : prevState.currentFrame,
                    firstPlayer: (game?.first_player_name && game.first_player_name !== 'Player A') ? game.first_player_name : (incomingState.first_player && incomingState.first_player.trim() !== '' ? incomingState.first_player : prevState.firstPlayer),
                    secondPlayer: (game?.second_player_name && game.second_player_name !== 'Player B') ? game.second_player_name : (incomingState.second_player && incomingState.second_player.trim() !== '' ? incomingState.second_player : prevState.secondPlayer),
                    sequence: incomingState.sequence,
                    timestamp: incomingState.timestamp
                };
                let newState;
                if (type === 'snooker') {
                    const historyEffect = classifyRemoteFrameUpdate(prevState, incomingState);
                    newState = {
                        ...prevState,
                        ...baseUpdate,
                        player1Score: incomingState.first_player_score !== undefined ? incomingState.first_player_score : prevState.player1Score,
                        player2Score: incomingState.second_player_score !== undefined ? incomingState.second_player_score : prevState.player2Score,
                        currentBreakPoints: incomingState.current_break_points !== undefined ? incomingState.current_break_points : prevState.currentBreakPoints,
                        currentBreakPottedBalls: Array.isArray(incomingState.current_break_potted_balls) ? incomingState.current_break_potted_balls : prevState.currentBreakPottedBalls,
                        redsRemaining: incomingState.reds_remaining !== undefined ? incomingState.reds_remaining : prevState.redsRemaining,
                        colorsRemaining: incomingState.colors_remaining && typeof incomingState.colors_remaining === 'object' ? incomingState.colors_remaining : prevState.colorsRemaining,
                        colorsPottedFirstTime: incomingState.colors_potted_first_time && typeof incomingState.colors_potted_first_time === 'object' ? incomingState.colors_potted_first_time : prevState.colorsPottedFirstTime,
                        player1Break: incomingState.first_player_break !== undefined ? incomingState.first_player_break : prevState.player1Break,
                        player2Break: incomingState.second_player_break !== undefined ? incomingState.second_player_break : prevState.player2Break,
                        player1PottedBalls: Array.isArray(incomingState.first_player_potted_balls) ? incomingState.first_player_potted_balls : prevState.player1PottedBalls,
                        player2PottedBalls: Array.isArray(incomingState.second_player_potted_balls) ? incomingState.second_player_potted_balls : prevState.player2PottedBalls,
                        timerStartTimestamp: incomingState.timer_start_timestamp !== undefined ? incomingState.timer_start_timestamp : prevState.timerStartTimestamp,
                        timerPausedDuration: incomingState.timer_paused_duration !== undefined ? incomingState.timer_paused_duration : prevState.timerPausedDuration,
                        timerIsRunning: incomingState.timer_is_running !== undefined ? incomingState.timer_is_running : prevState.timerIsRunning,
                        freeBallActive: incomingState.free_ball_active !== undefined ? incomingState.free_ball_active : prevState.freeBallActive,
                        streamingStatsCommand: incomingState.streaming_stats_command !== undefined ? incomingState.streaming_stats_command : prevState.streamingStatsCommand,
                        selectingFoulValue: incomingState.selecting_foul_value !== undefined ? incomingState.selecting_foul_value : prevState.selectingFoulValue,
                        frameOver: incomingState.frame_over !== undefined ? incomingState.frame_over : prevState.frameOver,
                        lastRedPotted: incomingState.last_red_potted !== undefined ? incomingState.last_red_potted : prevState.lastRedPotted,
                        firstColorAfterLastRedPotted: incomingState.first_color_after_last_red_potted !== undefined ? incomingState.first_color_after_last_red_potted : prevState.firstColorAfterLastRedPotted,
                        finalColorsPhase: incomingState.final_colors_phase !== undefined ? incomingState.final_colors_phase : prevState.finalColorsPhase,
                        maxPoints: incomingState.max_points !== undefined ? incomingState.max_points : prevState.maxPoints,
                        shotRemaining: incomingState.shot_remaining !== undefined ? incomingState.shot_remaining : prevState.shotRemaining,
                        currentShotLimitMs: incomingState.current_shot_limit_ms !== undefined ? incomingState.current_shot_limit_ms : prevState.currentShotLimitMs,
                        shotTimerIsRunning: incomingState.shot_timer_is_running !== undefined ? incomingState.shot_timer_is_running : prevState.shotTimerIsRunning,
                        shotStartTimestamp: incomingState.shot_start_timestamp !== undefined ? incomingState.shot_start_timestamp : prevState.shotStartTimestamp,
                        frameExpired: incomingState.frame_expired !== undefined ? incomingState.frame_expired : prevState.frameExpired,
                        timerOwner: incomingState.timer_owner !== undefined ? incomingState.timer_owner : prevState.timerOwner,
                        timerSyncEnabled: incomingState.timer_sync_enabled !== undefined ? incomingState.timer_sync_enabled : prevState.timerSyncEnabled,
                        frameStartTimestamp: incomingState.frame_start_timestamp !== undefined
                            ? incomingState.frame_start_timestamp
                            : (historyEffect.reset ? null : prevState.frameStartTimestamp),
                        switchCount: incomingState.switch_count !== undefined
                            ? incomingState.switch_count
                            : (historyEffect.reset ? 0 : prevState.switchCount),
                    };

                    if (historyEffect.reset) {
                        resetFrameEvents();
                    }
                    if (historyEffect.incomplete) {
                        frameEventsTruncatedRef.current = true;
                    }
                } else {
                    newState = {
                        ...prevState,
                        ...baseUpdate,
                        totalSeconds: incomingState.total_seconds !== undefined ? incomingState.total_seconds : prevState.totalSeconds,
                        shotTimeRemaining: shotTimerOwner !== 'local' && incomingState.shot_time_remaining !== undefined ? incomingState.shot_time_remaining : prevState.shotTimeRemaining,
                        player1ExtensionUsed: incomingState.player1_extension_used !== undefined ? incomingState.player1_extension_used : prevState.player1ExtensionUsed,
                        player2ExtensionUsed: incomingState.player2_extension_used !== undefined ? incomingState.player2_extension_used : prevState.player2ExtensionUsed,
                        isGameFinished: incomingState.is_game_finished !== undefined ? incomingState.is_game_finished : prevState.isGameFinished,
                        winMessage: incomingState.win_message || prevState.winMessage,
                        time: incomingState.total_seconds !== undefined ? formatTime(incomingState.total_seconds * 1000) : prevState.time,
                    };
                    if (type === 'chinese-pool') {
                        newState.player1IllegalBreaks = incomingState.player1_illegal_breaks !== undefined ? incomingState.player1_illegal_breaks : prevState.player1IllegalBreaks;
                        newState.player2IllegalBreaks = incomingState.player2_illegal_breaks !== undefined ? incomingState.player2_illegal_breaks : prevState.player2IllegalBreaks;
                    }
                }
                isUpdatingFromSocketRef.current = false;
                lastEmittedPayloadRef.current = buildGameStatePayload(newState, game.id);
                setTimeout(updateDisplayTime, 0);
                return newState;
            });
            updateDisplayTime();
        }
    };
    const syncStateFromSocket = useCallback((incomingState) => {
        syncStateFromSocketRef.current(incomingState);
    }, []);
    const emitGameState = useCallback((baseState, isCritical = false, extraPayload = {}) => {
        if (!socket || !hasReceivedInitialData || !game?.id || !socket.connected) return;

        if (baseState.sequence > lastAcceptedSequenceRef.current) {
            lastAcceptedSequenceRef.current = baseState.sequence;
        }

        const now = Date.now();
        const payload = buildGameStatePayload(baseState, game.id);
        const fullPayload = { ...payload, ...extraPayload };

        if (isCritical) {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }

            socket.emit('gameStateUpdate', fullPayload);
            lastEmittedPayloadRef.current = fullPayload;
            lastEmittedTimestampRef.current = now;
            return;
        }

        const timeSinceLastEmit = now - lastEmittedTimestampRef.current;

        if (isLocalActionRef.current || timeSinceLastEmit >= 500) {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

            debounceTimeoutRef.current = setTimeout(() => {
                if (!shallowEqual(fullPayload, lastEmittedPayloadRef.current)) {
                    socket.emit('gameStateUpdate', fullPayload);
                    lastEmittedPayloadRef.current = fullPayload;
                    lastEmittedTimestampRef.current = Date.now();
                }
                debounceTimeoutRef.current = null;
            }, 100);
            return;
        }

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            const currentFullPayload = { ...buildGameStatePayload(baseState, game.id), ...extraPayload };
            if (!shallowEqual(currentFullPayload, lastEmittedPayloadRef.current)) {
                socket.emit('gameStateUpdate', currentFullPayload);
                lastEmittedPayloadRef.current = currentFullPayload;
                lastEmittedTimestampRef.current = Date.now();
            }
            debounceTimeoutRef.current = null;
        }, 100);
    }, [socket, hasReceivedInitialData, game?.id, buildGameStatePayload]);

    const potBall = (ball, options) => {
        if (isGameFinished || type !== 'snooker') return;
        markLocalAction();
        setGameState(prevState => {
            if (options?.freeOnly && !prevState.freeBallActive) return prevState;
            if (prevState.selectingFoulValue) return awardFoul(prevState, Math.max(minimumFoulPoints(prevState), SNOOKER_COLORS[ball] || 1));
            const scored = potSnookerBall(prevState, ball, options);
            if (scored === prevState) return prevState;
            const player = prevState.currentPlayer;
            const points = scored[`player${player}Score`] - prevState[`player${player}Score`];
            const newState = {
                ...scored,
                frameStartTimestamp: prevState.frameStartTimestamp || Date.now(),
                sequence: prevState.sequence + 1,
                timestamp: Date.now(),
            };
            if (!prevState.frameStartTimestamp) {
                recordFrameEvent(newState.sequence, 'frame_started', { player });
            }
            // Mode selection is not a scored/undoable event. A free-ball pot is
            // recorded with its actual awarded points for backend replay.
            recordFrameEvent(newState.sequence, 'pot', {
                player,
                ball: prevState.freeBallActive ? 'free_ball' : ball.toLowerCase(),
                points,
                score_after: { player_1: newState.player1Score, player_2: newState.player2Score },
                reds_remaining: newState.redsRemaining,
                final_colours_phase: !!newState.finalColorsPhase,
            });
            emitGameState(newState, true);
            pushAction({
                action: ball === 'Red' ? 'potRed' : 'potColor',
                player,
                points,
                scoringBefore: snookerScoringSnapshot(prevState),
            });
            return newState;
        });
    };
    const potRed = () => potBall('Red');
    const potColor = (colorName) => potBall(colorName);
    const potFreeBall = (both = false) => potBall('Red', { both, freeOnly: true });
    const adjustReds = (delta) => {
        if (isGameFinished || type !== 'snooker') return;
        markLocalAction();
        setGameState(prevState => {
            const adjusted = adjustSnookerReds(prevState, delta);
            if (adjusted === prevState) return prevState;
            const newState = { ...adjusted, maxPoints: snookerPointsRemaining(adjusted),
                sequence: prevState.sequence + 1, timestamp: Date.now() };
            recordFrameEvent(newState.sequence, 'reds_adjusted', {
                player: prevState.currentPlayer, reds_remaining: newState.redsRemaining,
                final_colours_phase: newState.finalColorsPhase,
                score_after: { player_1: newState.player1Score, player_2: newState.player2Score },
            });
            pushAction({ action: 'adjustReds', player: prevState.currentPlayer,
                redsBefore: snookerRedsSnapshot(prevState) });
            emitGameState(newState, true);
            return newState;
        });
    };
    const switchPlayer = () => {
        if (isGameFinished) return;
        setGameState(prevState => {
            const prevCurrentPlayer = prevState.currentPlayer;
            const prevCurrentBreakPoints = prevState.currentBreakPoints;
            if (type === 'snooker' && prevCurrentBreakPoints > 0) {
                setFrameBreaks(prev => [
                    ...prev,
                    { currentPlayer: prevCurrentPlayer, break: prevCurrentBreakPoints }
                ]);
            }
            let frameElapsed;
            if (type === 'snooker') {
                frameElapsed = (!prevState.timerIsRunning || !prevState.timerStartTimestamp)
                    ? prevState.timerPausedDuration
                    : (prevState.timerPausedDuration + (Date.now() - prevState.timerStartTimestamp));
            } else {
                frameElapsed = prevState.totalSeconds * 1000;
            }
            const shotLimitMs = frameElapsed < 300000 ? 15000 : 10000;
            let shotUpdates = {};
            if (type === 'snooker' && prevState.shotTimerIsRunning) {
                const now = Date.now();
                const elapsed = now - prevState.shotStartTimestamp;
                const newShotRemaining = Math.max(0, prevState.shotRemaining - elapsed);
                shotUpdates = {
                    shotTimerIsRunning: false,
                    shotStartTimestamp: null,
                    shotRemaining: newShotRemaining
                };
            }
            const updates = {
                currentPlayer: prevState.currentPlayer === 1 ? 2 : 1,
                player1Break: type === 'snooker' && prevState.currentPlayer === 1 ? 0 : prevState.player1Break,
                player2Break: type === 'snooker' && prevState.currentPlayer === 2 ? 0 : prevState.player2Break,
                freeBallActive: type === 'snooker' ? false : prevState.freeBallActive,
                selectingFoulValue: type === 'snooker' ? false : prevState.selectingFoulValue,
                firstColorAfterLastRedPotted: type === 'snooker' && prevState.lastRedPotted && !prevState.firstColorAfterLastRedPotted ? true : prevState.firstColorAfterLastRedPotted,
                finalColorsPhase: type === 'snooker' && prevState.lastRedPotted && !prevState.firstColorAfterLastRedPotted ? true : prevState.finalColorsPhase,
                currentBreakPoints: type === 'snooker' ? 0 : prevState.currentBreakPoints,
                currentBreakPottedBalls: type === 'snooker' ? [] : prevState.currentBreakPottedBalls || [],
                shotTimeRemaining: type !== 'snooker' ? (parseInt(getShotTimeSetting()) || 45) : prevState.shotTimeRemaining,
                timerOwner: (type === 'snooker' && prevState.timerSyncEnabled) ? (prevState.currentPlayer === 1 ? 2 : 1) : prevState.timerOwner,
                shotRemaining: type === 'snooker' ? shotLimitMs : prevState.shotRemaining,
                shotTimerIsRunning: false,
                shotStartTimestamp: null,
                currentShotLimitMs: type === 'snooker' ? shotLimitMs : prevState.currentShotLimitMs,
                switchCount: type === 'snooker' ? (prevState.switchCount + 1) : prevState.switchCount,
                ...shotUpdates,
            };
            const newState = { ...prevState, ...updates, sequence: prevState.sequence + 1, timestamp: Date.now() };
            if (type !== 'snooker' && shotTimer) {
                clearInterval(shotTimer);
                setShotTimer(null);
            }
            recordFrameEvent(newState.sequence, 'player_switched', { player: prevCurrentPlayer });
            emitGameState(newState, true);
            pushAction({
                action: 'switchPlayer',
                player: prevCurrentPlayer,
                ...(type === 'snooker' ? {
                    scoringBefore: snookerScoringSnapshot(prevState),
                    frameBreaksBefore: [...frameBreaks],
                } : {}),
                player1Break: prevState.player1Break,
                player2Break: prevState.player2Break,
                currentBreakPoints: prevCurrentBreakPoints,
                currentBreakPottedBalls: type === 'snooker' ? [...(prevState.currentBreakPottedBalls || [])] : [],
            });
            return newState;
        });
    };
    const startTimer = () => {
        if (isGameFinished) return;
        markLocalAction();
        if (type === 'snooker') {
            if (!gameState.timerIsRunning) {
                const now = Date.now();
                setGameState(prevState => {
                    const newState = {
                        ...prevState,
                        timerStartTimestamp: now,
                        timerIsRunning: true,
                        sequence: prevState.sequence + 1,
                        timestamp: Date.now()
                    };
                    emitGameState(newState, true);
                    return newState;
                });
                updateDisplayTime();
            }
        } else {
            if (frameTimer === null) {
                setGameState(prevState => {
                    const newState = {
                        ...prevState,
                        timerIsRunning: true,
                        sequence: prevState.sequence + 1,
                        timestamp: Date.now()
                    };
                    emitGameState(newState, true);
                    return newState;
                });
            }
        }
    };
    const pauseTimer = () => {
        if (isGameFinished) return;
        markLocalAction();
        if (type === 'snooker') {
            stopShotTimer();
            if (!gameState.timerIsRunning) {
                if (frameTimer !== null) {
                    clearInterval(frameTimer);
                    setFrameTimer(null);
                }
                return;
            }
            setGameState(prevState => {
                const now = Date.now();
                const elapsedSinceStart = now - prevState.timerStartTimestamp;
                let newState = {
                    ...prevState,
                    timerIsRunning: false,
                    timerPausedDuration: prevState.timerPausedDuration + elapsedSinceStart,
                    timerStartTimestamp: null,
                    sequence: prevState.sequence + 1,
                    timestamp: Date.now()
                };
                if (prevState.shotTimerIsRunning) {
                    const shotElapsed = now - prevState.shotStartTimestamp;
                    const newShotRemaining = Math.max(0, prevState.shotRemaining - shotElapsed);
                    newState = {
                        ...newState,
                        shotTimerIsRunning: false,
                        shotStartTimestamp: null,
                        shotRemaining: newShotRemaining
                    };
                }
                emitGameState(newState, true);
                return newState;
            });
            updateDisplayTime();
        } else {
            if (!gameState.timerIsRunning) {
                return;
            }
            setGameState(prevState => {
                const newState = {
                    ...prevState,
                    timerIsRunning: false,
                    sequence: prevState.sequence + 1,
                    timestamp: Date.now()
                };
                emitGameState(newState, true);
                return newState;
            });
        }
        if (frameTimer !== null) {
            clearInterval(frameTimer);
            setFrameTimer(null);
        }
    };
    const resetTimer = () => {
        if (isGameFinished) return;
        markLocalAction();
        if (type === 'snooker') {
            setGameState(prevState => {
                const newState = {
                    ...prevState,
                    timerStartTimestamp: null,
                    timerPausedDuration: 0,
                    timerIsRunning: false,
                    shotRemaining: 15000,
                    shotTimerIsRunning: false,
                    shotStartTimestamp: null,
                    currentShotLimitMs: 15000,
                    frameExpired: false,
                    sequence: prevState.sequence + 1,
                    timestamp: Date.now()
                };
                emitGameState(newState, true);
                return newState;
            });
            setDisplayTime(getShotTimerEnabled() ? '00:10:00' : '00:00:00');
        } else {
            setGameState(prevState => {
                const newState = {
                    ...prevState,
                    totalSeconds: 0,
                    time: '00 : 00 : 00',
                    timerIsRunning: false,
                    sequence: prevState.sequence + 1,
                    timestamp: Date.now()
                };
                emitGameState(newState, true);
                return newState;
            });
        }
        if (frameTimer !== null) {
            clearInterval(frameTimer);
            setFrameTimer(null);
        }
    };
    const freeBall = () => {
        if (isGameFinished || type !== 'snooker') return;
        markLocalAction();
        setGameState(prevState => {
            if (prevState.frameOver || !freeBallTarget(prevState)) return prevState;
            const newState = {
                ...prevState,
                freeBallActive: !prevState.freeBallActive,
                selectingFoulValue: false,
                sequence: prevState.sequence + 1,
                timestamp: Date.now()
            };
            emitGameState(newState, true);
            return newState;
        });
    };
    const addFoul = () => {
        if (isGameFinished || type !== 'snooker') return;
        markLocalAction();
        setGameState(prevState => {
            if (prevState.frameOver) return prevState;
            const newState = { ...prevState, selectingFoulValue: !prevState.selectingFoulValue, sequence: prevState.sequence + 1, timestamp: Date.now() };
            emitGameState(newState, true);
            return newState;
        });
    };
    const controlStreamingStats = (action) => {
        if (type !== 'snooker' || !['show', 'hide', 'resume'].includes(action)) return;
        if (!socket?.connected || !hasReceivedInitialDataRef.current || isInitializingRef.current) {
            socket?.connect();
            return;
        }
        setGameState(prevState => {
            const now = Date.now();
            const newState = {
                ...prevState,
                streamingStatsCommand: { action, expiresAt: now + STATS_DURATION_MS, frame: statsFrameKey(prevState) },
                sequence: Math.max(prevState.sequence, lastAcceptedSequenceRef.current) + 1,
                timestamp: now,
            };
            // Presentation only: no score, turn, timer or Undo event.
            emitGameState(newState, true);
            return newState;
        });
    };
    const cancelScoringMode = () => {
        markLocalAction();
        setGameState(prevState => {
            const newState = { ...prevState, selectingFoulValue: false, freeBallActive: false,
                sequence: prevState.sequence + 1, timestamp: Date.now() };
            emitGameState(newState, true);
            return newState;
        });
    };
    const awardFoul = (prevState, points) => {
        const awarded = applySnookerFoul(prevState, points);
        if (awarded === prevState) return prevState;
        const newState = {
            ...awarded,
            frameStartTimestamp: prevState.frameStartTimestamp || Date.now(),
            sequence: prevState.sequence + 1,
            timestamp: Date.now()
        };
        if (!prevState.frameStartTimestamp) {
            recordFrameEvent(newState.sequence, 'frame_started', { player: prevState.currentPlayer });
        }
        // Backend foul events identify the recipient, not the offender.
        recordFrameEvent(newState.sequence, 'foul', {
            player: newState.currentPlayer,
            points,
            score_after: { player_1: newState.player1Score, player_2: newState.player2Score },
        });
        if (prevState.currentBreakPoints > 0) {
            setFrameBreaks(prev => [...prev, { currentPlayer: prevState.currentPlayer, break: prevState.currentBreakPoints }]);
        }
        emitGameState(newState, true);
        pushAction({
            action: 'addPoints',
            player: prevState.currentPlayer,
            points,
            scoringBefore: { ...snookerScoringSnapshot(prevState), selectingFoulValue: false },
            frameBreaksBefore: [...frameBreaks],
        });
        return newState;
    };
    const addPoints = (points) => {
        if (isGameFinished || type !== 'snooker') return;
        markLocalAction();
        setGameState(prevState => awardFoul(prevState, points));
    };
    // Which player the frame is credited to. In snooker the frame goes to the
    // player with the bigger frame score, not to the player currently at the
    // table. Scores level (including 0-0, i.e. the button pressed before
    // anything was potted) falls back to the selected player. The pool variants
    // have no per-frame points at all - their displayed score IS framesWon - so
    // they keep using the selected player.
    const getFrameWinnerPlayer = (state) => {
        if (type !== 'snooker') return state.currentPlayer;
        if (state.player1Score > state.player2Score) return 1;
        if (state.player2Score > state.player1Score) return 2;
        return state.currentPlayer;
    };
    const cancelCompletionAfterStore = (action) => {
        if (!action?.clientFrameId || !action?.cancelPayload || cancelledCompletionIdsRef.current.has(action.clientFrameId)) {
            return;
        }
        cancelledCompletionIdsRef.current.add(action.clientFrameId);
        const storePromise = frameStorePromisesRef.current.get(action.clientFrameId) || Promise.resolve();
        const endpoint = isPublic ? '/public/frame/cancel' : '/club/frame/cancel';
        Promise.resolve(storePromise)
            .catch(() => null)
            .then(() => apiService.postDurable(endpoint, action.cancelPayload))
            .catch(error => console.error('Error cancelling frame completion:', error));
    };
    const winFrame = async () => {
        if (isGameFinished || isWinningFrameRef.current) return;
        isWinningFrameRef.current = true;
        const framePlayer = getFrameWinnerPlayer(gameState);
        let paid = true;
        if (user?.code_activation === '1') {
            const who = framePlayer === 1 ? gameState.firstPlayer : gameState.secondPlayer;
            const code = typeof window !== 'undefined' ? window.prompt(`Enter password to win frame for ${who}`) : null;
            if (code === null) {
                isWinningFrameRef.current = false;
                return;
            }
            try {
                const passData = await apiService.post('/club/check-code', {
                    id: user.id,
                    code
                });
                if (!passData?.success) {
                    alert(passData?.message || 'Invalid password. Please try again.');
                    isWinningFrameRef.current = false;
                    return;
                }
                paid = !!passData.paid;
            } catch {
                alert('An error occurred. Please try again.');
                isWinningFrameRef.current = false;
                return;
            }
        }
        markLocalAction();
        const previousState = cloneGameState(gameState);
        const frameEventsSnapshot = snapshotFrameEvents();
        const frameBreaksSnapshot = [...frameBreaks];
        const clientFrameId = createClientFrameId();
        const completionReason = 'win_frame';
        const cancelPayload = {
            client_frame_id: clientFrameId,
            game_id: game.id,
            game_link: linkId,
        };
        try {
            const frameData = {
                id_user: user?.id,
                paid: paid,
                game_id: game.id,
                game_link: linkId,
                client_frame_id: clientFrameId,
                completion_reason: completionReason,
                is_guest: game.is_guest,
                frame_nb: gameState.currentFrame,
                winner: framePlayer === 1 ? game.first_player : game.second_player,
            };
            if (type === 'snooker') {
                const frameStartTimestamp = resolveFrameStartTimestamp(
                    frameEventsSnapshot.events,
                    gameState.frameStartTimestamp,
                    gameState.timerStartTimestamp,
                );
                const savedBreaks = [...frameBreaksSnapshot];
                if (gameState.currentBreakPoints > 0) {
                    savedBreaks.push({ currentPlayer: gameState.currentPlayer, break: gameState.currentBreakPoints });
                }
                frameData.first_player_score = gameState.player1Score;
                frameData.second_player_score = gameState.player2Score;
                frameData.breaks = JSON.stringify(savedBreaks);
                frameData.frame_start_ts = frameStartTimestamp;
                const potCount = (gameState.player1PottedBalls?.length || 0) + (gameState.player2PottedBalls?.length || 0);
                const eventActionCount = frameEventsSnapshot.events.filter(event => event.type !== 'frame_started').length;
                frameData.actions_count = Math.max(eventActionCount, potCount + (gameState.switchCount || 0));
                // Only close a frame that actually recorded something: a lone
                // frame_ended event would read as a one-action frame, when in
                // truth this client simply has no history to offer.
                if (frameEventsSnapshot.events.length) {
                    recordFrameEvent((gameState.sequence || 0) + 1, 'frame_ended', {
                        player: framePlayer,
                        completion_reason: completionReason,
                        score_after: { player_1: gameState.player1Score, player_2: gameState.player2Score },
                    });
                }
                const events = buildFrameEventsPayload(frameStartTimestamp);
                if (events) {
                    frameData.events = events;
                }
            }
            const endpoint = isPublic ? '/public/frame/store' : '/club/frame/store';
            const storePromise = apiService.postDurable(endpoint, frameData).catch(err => {
                console.error('Error storing frame in background:', err);
                return { success: false, error: err };
            });
            frameStorePromisesRef.current.set(clientFrameId, storePromise);
            if (type !== 'snooker' && shotTimer) {
                clearInterval(shotTimer);
                setShotTimer(null);
            }
            setGameState(prevState => {
                const updates = {
                    player1FramesWon: framePlayer === 1 ? prevState.player1FramesWon + 1 : prevState.player1FramesWon,
                    player2FramesWon: framePlayer === 2 ? prevState.player2FramesWon + 1 : prevState.player2FramesWon,
                    currentFrame: prevState.currentFrame + 1,
                };
                const newPlayer1Frames = framePlayer === 1 ? prevState.player1FramesWon + 1 : prevState.player1FramesWon;
                const newPlayer2Frames = framePlayer === 2 ? prevState.player2FramesWon + 1 : prevState.player2FramesWon;
                const isRace = game?.is_race == 1 || game?.is_race === '1';
                const raceTo = parseInt(game?.race_to);
                if (isRace && raceTo && (newPlayer1Frames >= raceTo || newPlayer2Frames >= raceTo)) {
                    const winner = newPlayer1Frames >= raceTo ? gameState.firstPlayer : gameState.secondPlayer;
                    updates.isGameFinished = true;
                    updates.winMessage = `${winner} wins the match! Waiting for next match...`;
                    setIsGameFinished(true);
                    setWinMessage(`${winner} wins the match! Waiting for next match...`);
                    const winnerId = newPlayer1Frames >= raceTo ? game.first_player : game.second_player;
                    setGameWinner(winnerId);
                }
                if (type === 'snooker') {
                    if (prevState.shotTimerIsRunning) {
                        updates.shotTimerIsRunning = false;
                        updates.shotStartTimestamp = null;
                    }
                }
                if (type === 'snooker') {
                    updates.player1Score = 0;
                    updates.player2Score = 0;
                    updates.player1PottedBalls = [];
                    updates.player2PottedBalls = [];
                    updates.currentBreakPoints = 0;
                    updates.redsRemaining = 15;
                    updates.colorsRemaining = { Yellow: 2, Green: 3, Brown: 4, Blue: 5, Pink: 6, Black: 7 };
                    updates.colorsPottedFirstTime = { Yellow: false, Green: false, Brown: false, Blue: false, Pink: false, Black: false };
                    updates.player1Break = 0;
                    updates.player2Break = 0;
                    updates.currentBreakPottedBalls = [];
                    updates.maxPoints = 147;
                    updates.freeBallActive = false;
                    updates.selectingFoulValue = false;
                    updates.frameOver = false;
                    updates.lastRedPotted = false;
                    updates.firstColorAfterLastRedPotted = false;
                    updates.finalColorsPhase = false;
                    updates.timerStartTimestamp = null;
                    updates.timerPausedDuration = 0;
                    updates.timerIsRunning = false;
                    updates.shotRemaining = 15000;
                    updates.frameExpired = false;
                    updates.frameStartTimestamp = null;
                    updates.switchCount = 0;
                    updates.timerOwner = prevState.currentPlayer;
                } else {
                    updates.shotTimeRemaining = parseInt(getShotTimeSetting()) || 45;
                }
                const newState = {
                    ...prevState,
                    ...updates,
                    sequence: prevState.sequence + 1,
                    timestamp: Date.now()
                };
                emitGameState(newState, true, type !== 'snooker' ? { shot_timer_command: 'stop' } : {});
                return newState;
            });
            pushAction({
                action: 'winFrame',
                previousState,
                frameEventsSnapshot,
                frameBreaksBefore: frameBreaksSnapshot,
                clientFrameId,
                completionReason,
                cancelPayload,
                player: gameState.currentPlayer,
                player1FramesWon: gameState.player1FramesWon,
                player2FramesWon: gameState.player2FramesWon,
                player1Score: gameState.player1Score,
                player2Score: gameState.player2Score,
                player1PottedBalls: gameState.player1PottedBalls,
                player2PottedBalls: gameState.player2PottedBalls,
                currentBreakPoints: gameState.currentBreakPoints,
                currentBreakPottedBalls: gameState.currentBreakPottedBalls,
                redsRemaining: gameState.redsRemaining,
                colorsRemaining: gameState.colorsRemaining,
                colorsPottedFirstTime: gameState.colorsPottedFirstTime,
                player1Break: gameState.player1Break,
                player2Break: gameState.player2Break,
                freeBallActive: gameState.freeBallActive,
                selectingFoulValue: gameState.selectingFoulValue,
                lastRedPotted: gameState.lastRedPotted,
                firstColorAfterLastRedPotted: gameState.firstColorAfterLastRedPotted,
                finalColorsPhase: gameState.finalColorsPhase,
                timerOwner: gameState.timerOwner,
                timerSyncEnabled: gameState.timerSyncEnabled,
                shotRemaining: gameState.shotRemaining,
                frameStartTimestamp: gameState.frameStartTimestamp,
                switchCount: gameState.switchCount,
            });
            if (type === 'snooker') {
                setDisplayTime(getShotTimerEnabled() ? '00:10:00' : '00:00:00');
            }

        } catch (error) {
            console.error('Error in winFrame:', error);
        } finally {
            isWinningFrameRef.current = false;
            setFrameBreaks([]);
            resetFrameEvents();
        }
    };
    const resetFrames = () => {
        // Deliberately NOT guarded on isGameFinished. Once a race is won the match
        // is over and every other control is locked, so resetting the frames is the
        // only way back to a playable board - guarding it here left the operator
        // stuck on the win message with no way out except editing the game to turn
        // the race off. The reset already clears isGameFinished and winMessage.
        markLocalAction();
        const previousState = cloneGameState(gameState);
        const frameEventsSnapshot = snapshotFrameEvents();
        const frameBreaksSnapshot = [...frameBreaks];
        const hasPlayedSnookerFrame = type === 'snooker' && (
            frameEventsSnapshot.events.length > 0
            || !!gameState.frameStartTimestamp
            || !!gameState.timerStartTimestamp
            || Number(gameState.player1Score || 0) !== 0
            || Number(gameState.player2Score || 0) !== 0
            || (gameState.player1PottedBalls?.length || 0) > 0
            || (gameState.player2PottedBalls?.length || 0) > 0
            || Number(gameState.currentBreakPoints || 0) > 0
            || Number(gameState.switchCount || 0) > 0
        );
        const resetAction = {
            action: 'resetFrames',
            player: gameState.currentPlayer,
            previousState,
            frameEventsSnapshot,
            frameBreaksBefore: frameBreaksSnapshot,
        };
        enqueueAction(async () => {
            try {
                if (hasPlayedSnookerFrame) {
                    const frameStartTimestamp = resolveFrameStartTimestamp(
                        frameEventsSnapshot.events,
                        gameState.frameStartTimestamp,
                        gameState.timerStartTimestamp,
                    );
                    const potCount = (gameState.player1PottedBalls?.length || 0) + (gameState.player2PottedBalls?.length || 0);
                    const eventActionCount = frameEventsSnapshot.events.filter(event => event.type !== 'frame_started').length;
                    const actions = Math.max(eventActionCount, potCount + (gameState.switchCount || 0));
                    const framePlayer = getFrameWinnerPlayer(gameState);
                    const clientFrameId = createClientFrameId();
                    const completionReason = 'reset_frames';
                    const savedBreaks = [...frameBreaksSnapshot];
                    if (gameState.currentBreakPoints > 0) {
                        savedBreaks.push({ currentPlayer: gameState.currentPlayer, break: gameState.currentBreakPoints });
                    }
                    if (frameEventsSnapshot.events.length) {
                        recordFrameEvent((gameState.sequence || 0) + 1, 'frame_ended', {
                            player: framePlayer,
                            completion_reason: completionReason,
                            score_after: { player_1: gameState.player1Score, player_2: gameState.player2Score },
                        });
                    }
                    const frameData = {
                        id_user: user?.id,
                        game_id: game.id,
                        game_link: linkId,
                        client_frame_id: clientFrameId,
                        completion_reason: completionReason,
                        is_guest: game.is_guest,
                        frame_nb: gameState.currentFrame,
                        winner: framePlayer === 1 ? game.first_player : game.second_player,
                        first_player_score: gameState.player1Score,
                        second_player_score: gameState.player2Score,
                        breaks: JSON.stringify(savedBreaks),
                        frame_start_ts: frameStartTimestamp,
                        actions_count: actions,
                    };
                    const events = buildFrameEventsPayload(frameStartTimestamp);
                    if (events) {
                        frameData.events = events;
                    }
                    const endpoint = isPublic ? '/public/frame/store' : '/club/frame/store';
                    const storePromise = apiService.postDurable(endpoint, frameData).catch(error => {
                        console.error('Error storing reset frame in background:', error);
                        return { success: false, error };
                    });
                    frameStorePromisesRef.current.set(clientFrameId, storePromise);
                    Object.assign(resetAction, {
                        clientFrameId,
                        completionReason,
                        cancelPayload: {
                            client_frame_id: clientFrameId,
                            game_id: game.id,
                            game_link: linkId,
                        },
                    });
                }
            } catch (e) { }
            const initialState = {
                player1FramesWon: 0,
                player2FramesWon: 0,
                currentFrame: 1,
                isGameFinished: false,
                winMessage: ''
            };
            if (type === 'snooker') {
                setGameState(prevState => {
                    const updates = {
                        ...initialState,
                        frameOver: false,
                        redsRemaining: 15,
                        colorsRemaining: { 'Yellow': 2, 'Green': 3, 'Brown': 4, 'Blue': 5, 'Pink': 6, 'Black': 7 },
                        colorsPottedFirstTime: { 'Yellow': false, 'Green': false, 'Brown': false, 'Blue': false, 'Pink': false, 'Black': false },
                        player1Score: 0,
                        player2Score: 0,
                        player1PottedBalls: [],
                        player2PottedBalls: [],
                        freeBallActive: false,
                        selectingFoulValue: false,
                        lastRedPotted: false,
                        firstColorAfterLastRedPotted: false,
                        finalColorsPhase: false,
                        currentBreakPoints: 0,
                        currentBreakPottedBalls: [],
                        timerStartTimestamp: null,
                        timerPausedDuration: 0,
                        timerIsRunning: false,
                        shotRemaining: 15000,
                        shotTimerIsRunning: false,
                        shotStartTimestamp: null,
                        frameExpired: false,
                        frameStartTimestamp: null,
                        switchCount: 0,
                    };
                    const newState = {
                        ...prevState,
                        ...updates,
                        sequence: prevState.sequence + 1,
                        timestamp: Date.now()
                    };
                    emitGameState(newState, true);
                    return newState;
                });
            } else {
                setGameState(prevState => {
                    const updates = {
                        ...initialState,
                        totalSeconds: 0,
                        time: '00 : 00 : 00',
                        shotTimeRemaining: parseInt(getShotTimeSetting()) || 45,
                        player1ExtensionUsed: false,
                        player2ExtensionUsed: false,
                    };
                    if (type === 'chinese-pool') {
                        updates.player1IllegalBreaks = 0;
                        updates.player2IllegalBreaks = 0;
                    }
                    const newState = {
                        ...prevState,
                        ...updates,
                        sequence: prevState.sequence + 1,
                        timestamp: Date.now()
                    };
                    emitGameState(newState, true);
                    return newState;
                });
            }
            if (shotTimer) {
                clearInterval(shotTimer);
                setShotTimer(null);
            }
            pushAction(resetAction);
            setFrameBreaks([]);
            resetFrameEvents();
            resetTimer();
            setIsGameFinished(false);
            setWinMessage(null);
        });
    };
    const addIllegalBreak = () => {
        if (isGameFinished || type !== 'chinese-pool') return;
        markLocalAction();
        enqueueAction(async () => {
            try {
                const currentPlayer = gameState.currentPlayer;
                const newIllegalBreaks = currentPlayer === 1
                    ? gameState.player1IllegalBreaks + 1
                    : gameState.player2IllegalBreaks + 1;
                if (newIllegalBreaks >= 3) {
                    const endpoint = isPublic ? '/public/frame/store' : '/club/frame/store';
                    await apiService.post(endpoint, {
                        id_user: user?.id,
                        paid: true,
                        game_id: game.id,
                        is_guest: game.is_guest,
                        frame_nb: gameState.currentFrame,
                        winner: currentPlayer === 1 ? game.second_player : game.first_player,
                    });
                    setGameState(prevState => {
                        const updates = {
                            player1FramesWon: currentPlayer === 2 ? prevState.player1FramesWon + 1 : prevState.player1FramesWon,
                            player2FramesWon: currentPlayer === 1 ? prevState.player2FramesWon + 1 : prevState.player2FramesWon,
                            player1IllegalBreaks: currentPlayer === 1 ? 0 : prevState.player1IllegalBreaks,
                            player2IllegalBreaks: currentPlayer === 2 ? 0 : prevState.player2IllegalBreaks,
                            shotTimeRemaining: parseInt(getShotTimeSetting()) || 45,
                        };
                        const newState = {
                            ...prevState,
                            ...updates,
                            sequence: prevState.sequence + 1,
                            timestamp: Date.now()
                        };
                        emitGameState(newState, true);
                        return newState;
                    });
                    pushAction({
                        action: 'winFrameByIllegalBreak',
                        player: currentPlayer === 1 ? 2 : 1,
                        player1FramesWon: currentPlayer === 2 ? gameState.player1FramesWon + 1 : gameState.player1FramesWon,
                        player2FramesWon: currentPlayer === 1 ? gameState.player2FramesWon + 1 : gameState.player2FramesWon,
                        player1IllegalBreaks: currentPlayer === 1 ? 0 : gameState.player1IllegalBreaks,
                        player2IllegalBreaks: currentPlayer === 2 ? 0 : gameState.player2IllegalBreaks,
                        player1Score: gameState.player1Score,
                        player2Score: gameState.player2Score,
                        player1PottedBalls: gameState.player1PottedBalls,
                        player2PottedBalls: gameState.player2PottedBalls,
                        currentBreakPoints: gameState.currentBreakPoints,
                        currentBreakPottedBalls: gameState.currentBreakPottedBalls,
                        redsRemaining: gameState.redsRemaining,
                        colorsRemaining: gameState.colorsRemaining,
                        colorsPottedFirstTime: gameState.colorsPottedFirstTime,
                        player1Break: gameState.player1Break,
                        player2Break: gameState.player2Break,
                        freeBallActive: gameState.freeBallActive,
                        selectingFoulValue: gameState.selectingFoulValue,
                        lastRedPotted: gameState.lastRedPotted,
                        firstColorAfterLastRedPotted: gameState.firstColorAfterLastRedPotted,
                        finalColorsPhase: gameState.finalColorsPhase,
                        timerOwner: gameState.timerOwner,
                        timerSyncEnabled: gameState.timerSyncEnabled,
                        shotRemaining: gameState.shotRemaining,
                        frameStartTimestamp: gameState.frameStartTimestamp,
                        switchCount: gameState.switchCount,
                    });
                    if (shotTimer) {
                        clearInterval(shotTimer);
                        setShotTimer(null);
                    }
                    pauseTimer();
                } else {
                    setGameState(prevState => {
                        const updates = {
                            player1IllegalBreaks: currentPlayer === 1 ? newIllegalBreaks : prevState.player1IllegalBreaks,
                            player2IllegalBreaks: currentPlayer === 2 ? newIllegalBreaks : prevState.player2IllegalBreaks,
                        };
                        const newState = {
                            ...prevState,
                            ...updates,
                            sequence: prevState.sequence + 1,
                            timestamp: Date.now()
                        };
                        emitGameState(newState, true);
                        return newState;
                    });
                    pushAction({
                        action: 'addIllegalBreak',
                        player: currentPlayer,
                        player1IllegalBreaks: currentPlayer === 1 ? newIllegalBreaks : gameState.player1IllegalBreaks,
                        player2IllegalBreaks: currentPlayer === 2 ? newIllegalBreaks : gameState.player2IllegalBreaks,
                    });
                }
            } catch (error) {
                console.error('Error in addIllegalBreak:', error);
            }
        });
    };
    const undoAction = () => {
        markLocalAction();
        if ((gameState.selectingFoulValue || gameState.freeBallActive) && actionStack.at(-1)?.action !== 'adjustReds') {
            cancelScoringMode();
            return;
        }
        enqueueAction(() => {
            if (actionStack.length === 0) {
                return;
            }
            const action = actionStack[actionStack.length - 1];
            if (action.frameBreaksBefore) setFrameBreaks(action.frameBreaksBefore);
            const isTerminalAction = action.action === 'winFrame' || action.action === 'resetFrames';
            if (isTerminalAction && action.frameEventsSnapshot) {
                restoreFrameEvents(action.frameEventsSnapshot);
                setFrameBreaks(action.frameBreaksBefore || []);
                cancelCompletionAfterStore(action);
                setIsGameFinished(!!action.previousState?.isGameFinished);
                setWinMessage(action.previousState?.winMessage || null);
                if (action.action === 'winFrame' && isGameFinished && !action.previousState?.isGameFinished) {
                    const winnerEndpoint = isPublic ? '/public/game/winner' : '/club/game/winner';
                    apiService.post(winnerEndpoint, { id: game.id, winner: null, undo: true })
                        .catch(error => console.error('Error reopening game after Undo:', error));
                }
            }
            setGameState(prevState => {
                if (action.redsBefore) {
                    const restored = { ...prevState, ...action.redsBefore };
                    const newState = { ...restored, maxPoints: snookerPointsRemaining(restored),
                        sequence: prevState.sequence + 1, timestamp: Date.now() };
                    recordFrameEvent(newState.sequence, 'undo', { player: action.player, undoes: 'reds_adjusted',
                        reds_remaining: newState.redsRemaining, final_colours_phase: newState.finalColorsPhase,
                        score_after: { player_1: newState.player1Score, player_2: newState.player2Score } });
                    emitGameState(newState, true);
                    return newState;
                }
                if (action.scoringBefore) {
                    const newState = { ...prevState, ...action.scoringBefore,
                        sequence: prevState.sequence + 1, timestamp: Date.now() };
                    recordFrameEvent(newState.sequence, 'undo', {
                        player: action.player,
                        undoes: action.action === 'addPoints' ? 'foul' : action.action === 'switchPlayer' ? 'player_switched' : 'pot',
                        score_after: { player_1: newState.player1Score, player_2: newState.player2Score },
                    });
                    emitGameState(newState, true);
                    return newState;
                }
                if (isTerminalAction && action.previousState) {
                    const newState = {
                        ...cloneGameState(action.previousState),
                        sequence: prevState.sequence + 1,
                        timestamp: Date.now(),
                    };
                    if (action.completionReason) {
                        recordFrameEvent(newState.sequence, 'undo', {
                            player: action.player,
                            undoes: action.completionReason,
                        });
                    }
                    emitGameState(newState, true);
                    return newState;
                }
                let updates = { currentPlayer: action.player };
                if (action.currentBreakPoints !== undefined) {
                    updates.currentBreakPoints = action.currentBreakPoints;
                }
                if (action.currentBreakPottedBalls !== undefined) {
                    updates.currentBreakPottedBalls = action.currentBreakPottedBalls;
                }
                updates.player1Break = action.player1Break;
                updates.player2Break = action.player2Break;
                if (action.action === 'potRed') {
                    updates.player1Score = action.player1Score;
                    updates.player2Score = action.player2Score;
                    updates.player1PottedBalls = action.player === 1 ? prevState.player1PottedBalls.slice(0, -1) : prevState.player1PottedBalls;
                    updates.player2PottedBalls = action.player === 2 ? prevState.player2PottedBalls.slice(0, -1) : prevState.player2PottedBalls;
                    updates.redsRemaining = action.redsRemaining;
                    updates.lastRedPotted = action.lastRedPotted;
                    updates.firstColorAfterLastRedPotted = action.firstColorAfterLastRedPotted;
                } else if (action.action === 'potColor') {
                    updates.player1Score = action.player1Score;
                    updates.player2Score = action.player2Score;
                    updates.player1PottedBalls = action.player === 1 ? prevState.player1PottedBalls.slice(0, -1) : prevState.player1PottedBalls;
                    updates.player2PottedBalls = action.player === 2 ? prevState.player2PottedBalls.slice(0, -1) : prevState.player2PottedBalls;
                    updates.firstColorAfterLastRedPotted = action.firstColorAfterLastRedPotted;
                    updates.finalColorsPhase = action.finalColorsPhase;
                    updates.colorsPottedFirstTime = action.colorsPottedFirstTime;
                } else if (action.action === 'switchPlayer') {
                    if (type === 'snooker' && (prevState.switchCount || 0) > 0) {
                        updates.switchCount = prevState.switchCount - 1;
                    }
                } else if (action.action === 'addPoints') {
                    updates.player1Score = action.player1Score;
                    updates.player2Score = action.player2Score;
                    updates.selectingFoulValue = action.selectingFoulValue;
                } else if (action.action === 'winFrame') {
                    updates.currentPlayer = action.player;
                    updates.player1FramesWon = action.player1FramesWon;
                    updates.player2FramesWon = action.player2FramesWon;
                    updates.currentFrame -= 1;
                    if (type === 'snooker') {
                        updates.player1Score = action.player1Score;
                        updates.player2Score = action.player2Score;
                        updates.player1PottedBalls = action.player1PottedBalls;
                        updates.player2PottedBalls = action.player2PottedBalls;
                        updates.currentBreakPoints = action.currentBreakPoints;
                        updates.currentBreakPottedBalls = action.currentBreakPottedBalls;
                        updates.redsRemaining = action.redsRemaining;
                        updates.colorsRemaining = action.colorsRemaining;
                        updates.colorsPottedFirstTime = action.colorsPottedFirstTime;
                        updates.player1Break = action.player1Break;
                        updates.player2Break = action.player2Break;
                        updates.freeBallActive = action.freeBallActive;
                        updates.selectingFoulValue = action.selectingFoulValue;
                        updates.lastRedPotted = action.lastRedPotted;
                        updates.firstColorAfterLastRedPotted = action.firstColorAfterLastRedPotted;
                        updates.finalColorsPhase = action.finalColorsPhase;
                        updates.timerOwner = action.timerOwner;
                        updates.timerSyncEnabled = action.timerSyncEnabled;
                        updates.shotRemaining = action.shotRemaining;
                        updates.frameStartTimestamp = action.frameStartTimestamp;
                        updates.switchCount = action.switchCount;
                        updates.isGameFinished = false;
                        updates.winMessage = '';
                    }
                } else if (action.action === 'addIllegalBreak') {
                    updates.player1IllegalBreaks = action.player1IllegalBreaks;
                    updates.player2IllegalBreaks = action.player2IllegalBreaks;
                } else if (action.action === 'winFrameByIllegalBreak') {
                    updates.currentPlayer = action.player;
                    updates.player1FramesWon = action.player1FramesWon;
                    updates.player2FramesWon = action.player2FramesWon;
                    updates.currentFrame -= 1;
                    updates.player1IllegalBreaks = action.player1IllegalBreaks || 0;
                    updates.player2IllegalBreaks = action.player2IllegalBreaks || 0;
                    if (type === 'snooker') {
                        updates.player1Score = action.player1Score;
                        updates.player2Score = action.player2Score;
                        updates.player1PottedBalls = action.player1PottedBalls;
                        updates.player2PottedBalls = action.player2PottedBalls;
                        updates.currentBreakPoints = action.currentBreakPoints;
                        updates.currentBreakPottedBalls = action.currentBreakPottedBalls;
                        updates.redsRemaining = action.redsRemaining;
                        updates.colorsRemaining = action.colorsRemaining;
                        updates.colorsPottedFirstTime = action.colorsPottedFirstTime;
                        updates.player1Break = action.player1Break;
                        updates.player2Break = action.player2Break;
                        updates.freeBallActive = action.freeBallActive;
                        updates.selectingFoulValue = action.selectingFoulValue;
                        updates.lastRedPotted = action.lastRedPotted;
                        updates.firstColorAfterLastRedPotted = action.firstColorAfterLastRedPotted;
                        updates.finalColorsPhase = action.finalColorsPhase;
                        updates.timerOwner = action.timerOwner;
                        updates.timerSyncEnabled = action.timerSyncEnabled;
                        updates.shotRemaining = action.shotRemaining;
                        updates.frameStartTimestamp = action.frameStartTimestamp;
                        updates.switchCount = action.switchCount;
                        updates.isGameFinished = false;
                        updates.winMessage = '';
                    }
                }
                const newState = { ...prevState, ...updates, sequence: prevState.sequence + 1, timestamp: Date.now() };
                recordFrameEvent(newState.sequence, 'undo', { player: action.player });
                emitGameState(newState, true);
                return newState;
            });
            setActionStack(previous => previous.slice(0, -1));
        });
    };
    const toggleTimerOwner = () => {
        if (isGameFinished || type !== 'snooker') return;
        markLocalAction();
        enqueueAction(() => {
            setGameState(prevState => {
                const newOwner = prevState.timerOwner === 1 ? 2 : 1;
                const frameElapsed = calculateCurrentTime();
                const shotLimitMs = frameElapsed < 300000 ? 15000 : 10000;
                const updates = {
                    timerOwner: newOwner,
                    shotRemaining: shotLimitMs,
                    shotTimerIsRunning: false,
                    shotStartTimestamp: null,
                    currentShotLimitMs: shotLimitMs,
                };
                if (prevState.timerSyncEnabled) {
                    updates.currentPlayer = newOwner;
                    updates.player1Break = prevState.currentPlayer === 1 ? 0 : prevState.player1Break;
                    updates.player2Break = prevState.currentPlayer === 2 ? 0 : prevState.player2Break;
                    updates.freeBallActive = false;
                    updates.selectingFoulValue = false;
                    updates.firstColorAfterLastRedPotted = prevState.lastRedPotted && !prevState.firstColorAfterLastRedPotted ? true : prevState.firstColorAfterLastRedPotted;
                    updates.finalColorsPhase = prevState.lastRedPotted && !prevState.firstColorAfterLastRedPotted ? true : prevState.finalColorsPhase;
                    updates.currentBreakPoints = 0;
                    updates.currentBreakPottedBalls = [];
                    updates.switchCount = prevState.switchCount + 1;
                }
                const newState = { ...prevState, ...updates, sequence: prevState.sequence + 1, timestamp: Date.now() };
                emitGameState(newState, true);
                return newState;
            });
        });
    };
    const toggleTimerSync = () => {
        if (isGameFinished || type !== 'snooker') return;
        markLocalAction();
        enqueueAction(() => {
            setGameState(prevState => {
                const newState = {
                    ...prevState,
                    timerSyncEnabled: !prevState.timerSyncEnabled,
                    sequence: prevState.sequence + 1,
                    timestamp: Date.now()
                };
                emitGameState(newState, true);
                return newState;
            });
        });
    };
    const toggleTimerSound = () => {
        if (isGameFinished || type !== 'snooker') return;
        markLocalAction();
        enqueueAction(() => {
            setGameState(prevState => {
                const newState = {
                    ...prevState,
                    timerSoundEnabled: !prevState.timerSoundEnabled,
                    sequence: prevState.sequence + 1,
                    timestamp: Date.now()
                };
                emitGameState(newState, true);
                return newState;
            });
        });
    };
    const startShotTimer = () => {
        if (!getShotTimerEnabled()) return;
        if (type === 'snooker') {
            if (!gameState.timerIsRunning) {
                startTimer();
            }
            if (isGameFinished || !getShotTimerEnabled()) return;
            markLocalAction();
            enqueueAction(() => {
                setGameState(prevState => {
                    if (prevState.shotTimerIsRunning) return prevState;
                    const now = Date.now();
                    const frameElapsed = calculateCurrentTime();
                    const limitSec = frameElapsed < 300000 ? 15 : 10;
                    const limitMs = limitSec * 1000;
                    const shotRemaining = prevState.shotRemaining > 0 ? Math.min(Math.max(prevState.shotRemaining, 0), limitMs) : limitMs;
                    const updates = {
                        shotRemaining,
                        shotStartTimestamp: now,
                        shotTimerIsRunning: true,
                        currentShotLimitMs: limitMs,
                    };
                    const newState = {
                        ...prevState,
                        ...updates,
                        sequence: prevState.sequence + 1,
                        timestamp: Date.now()
                    };
                    emitGameState(newState, true);
                    return newState;
                });
            });
        } else {
            if (isGameFinished || type === 'snooker' || !getShotTimerEnabled()) return;
            markLocalAction();
            enqueueAction(() => {
                if (shotTimer) {
                    clearInterval(shotTimer);
                    setShotTimer(null);
                }
                setShotTimerIsRunning(true);
                setShotTimerOwner('local');
                const timer = setInterval(() => {
                    setGameState(prevState => {
                        if (prevState.shotTimeRemaining <= 1) {
                            clearInterval(timer);
                            setShotTimer(null);
                            setShotTimerIsRunning(false);
                            setShotTimerOwner(null);
                            enqueueAction(switchPlayer);
                            const newState = {
                                ...prevState,
                                shotTimeRemaining: parseInt(getShotTimeSetting()) || 45,
                                sequence: prevState.sequence + 1,
                                timestamp: Date.now()
                            };
                            emitGameState(newState, false);
                            return newState;
                        }
                        const newState = {
                            ...prevState,
                            shotTimeRemaining: prevState.shotTimeRemaining - 1,
                            sequence: prevState.sequence + 1,
                            timestamp: Date.now()
                        };
                        emitGameState(newState, false);
                        return newState;
                    });
                }, 1000);
                setShotTimer(timer);
                setGameState(prevState => {
                    const newState = {
                        ...prevState,
                        sequence: prevState.sequence + 1,
                        timestamp: Date.now()
                    };
                    emitGameState(newState, true, { shot_timer_command: 'start' });
                    return newState;
                });
            });
        }
    };
    const stopShotTimer = () => {
        if (!getShotTimerEnabled()) return;
        if (type === 'snooker') {
            if (isGameFinished || !getShotTimerEnabled()) return;
            markLocalAction();
            enqueueAction(() => {
                setGameState(prevState => {
                    if (!prevState.shotTimerIsRunning) return prevState;
                    const now = Date.now();
                    const elapsed = now - prevState.shotStartTimestamp;
                    const newRemaining = Math.max(0, prevState.shotRemaining - elapsed);
                    const newState = {
                        ...prevState,
                        shotTimerIsRunning: false,
                        shotStartTimestamp: null,
                        shotRemaining: newRemaining,
                        currentShotLimitMs: null,
                        sequence: prevState.sequence + 1,
                        timestamp: Date.now()
                    };
                    emitGameState(newState, true);
                    return newState;
                });
            });
        } else {
            if (isGameFinished || type === 'snooker' || !getShotTimerEnabled()) return;
            markLocalAction();
            enqueueAction(() => {
                if (shotTimer) {
                    clearInterval(shotTimer);
                    setShotTimer(null);
                }
                setShotTimerIsRunning(false);
                setShotTimerOwner(null);
                setGameState(prevState => {
                    const newState = {
                        ...prevState,
                        sequence: prevState.sequence + 1,
                        timestamp: Date.now()
                    };
                    emitGameState(newState, true, { shot_timer_command: 'stop' });
                    return newState;
                });
            });
        }
    };
    const extendShotTimer = () => {
        if (isGameFinished || type === 'snooker' || !getShotTimerEnabled()) return;
        const currentPlayer = gameState.currentPlayer;
        const extensionUsed = currentPlayer === 1 ? gameState.player1ExtensionUsed : gameState.player2ExtensionUsed;
        if (extensionUsed) return;
        markLocalAction();
        enqueueAction(() => {
            setGameState(prevState => {
                const extensionTime = parseInt(settings[`${type.replace('-', '_')}_extension_time`]) || 15;
                const updates = {
                    shotTimeRemaining: prevState.shotTimeRemaining + extensionTime,
                };
                if (currentPlayer === 1) {
                    updates.player1ExtensionUsed = true;
                } else {
                    updates.player2ExtensionUsed = true;
                }
                const newState = {
                    ...prevState,
                    ...updates,
                    sequence: prevState.sequence + 1,
                    timestamp: Date.now()
                };
                emitGameState(newState, true);
                return newState;
            });
        });
    };
    const resetShotTimer = () => {
        if (isGameFinished || !getShotTimerEnabled()) return;
        markLocalAction();
        enqueueAction(() => {
            if (shotTimer) {
                clearInterval(shotTimer);
                setShotTimer(null);
            }
            setShotTimerIsRunning(false);
            setShotTimerOwner(null);
            setGameState(prevState => {
                const frameElapsed = calculateCurrentTime();
                const limitMs = frameElapsed < 300000 ? 15000 : 10000;
                const newState = {
                    ...prevState,
                    shotTimeRemaining: parseInt(getShotTimeSetting()) || 45,
                    shotRemaining: type === 'snooker' ? limitMs : prevState.shotRemaining,
                    shotTimerIsRunning: false,
                    shotStartTimestamp: null,
                    currentShotLimitMs: type === 'snooker' ? limitMs : prevState.currentShotLimitMs,
                    sequence: prevState.sequence + 1,
                    timestamp: Date.now()
                };
                emitGameState(newState, true, type !== 'snooker' ? { shot_timer_command: 'stop' } : {});
                return newState;
            });
        });
    };
    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(err => { });
        } else {
            document.exitFullscreen().catch(err => { });
        }
    };
    const forceSync = () => {
        if (!socket?.connected) {
            socket?.connect();
            return;
        }
        markLocalAction();
        setGameState(prevState => {
            // Advance the local state too, otherwise subsequent scoring updates
            // fall behind the forced revision and other screens ignore them.
            const forcedState = { ...prevState,
                sequence: Math.max(prevState.sequence || 0, lastAcceptedSequenceRef.current || 0) + 100,
                timestamp: Date.now() };
            emitGameState(forcedState, true);
            return forcedState;
        });
    };

    const keyDownFunction = useCallback((event) => {
        // Confirmation dialogs must not allow background scoring shortcuts.
        if (document.querySelector('[data-referee-dialog]')) return;
        // Leave normal button activation and navigation to the browser. Keep
        // the dedicated numpad Enter shortcut available to keypad operators.
        if (view === 'referee' && type === 'snooker'
            && event.target?.closest?.('button, summary')
            && ['Enter', ' ', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)
            && event.code !== 'NumpadEnter') return;
        if (event.repeat) return;

        // A modifier on its own is never an action, so it must not stamp the debounce
        // clock. On a main-row keyboard '+' is Shift+=, and the Shift that opens it
        // lands well inside the 150ms window - counting it swallowed the '+' that
        // followed and made the reset look dead.
        if (event.key === 'Shift' || event.key === 'Control' || event.key === 'Alt' || event.key === 'Meta' || event.key === 'CapsLock') return;

        const now = Date.now();
        if (now - lastKeyActionTimeRef.current < 150) {
            return;
        }
        lastKeyActionTimeRef.current = now;

        try {
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            if (event.target?.isContentEditable || ['input', 'textarea', 'select'].includes(event.target?.tagName?.toLowerCase())) return;
            if (type === 'snooker' && !isGameFinished) {
                const modeAction = { f: 'addFoul', b: 'freeBall', Escape: 'cancelScoringMode' }[event.key.length === 1 ? event.key.toLowerCase() : event.key];
                if (modeAction) {
                    event.preventDefault();
                    actionFunctionsRef.current[modeAction]();
                    return;
                }
            }
            // Once the race is won every action is blocked - except the reset. It is
            // the only way back to a playable board, and on a numpad-driven table the
            // + key is the operator's only route to it, so it has to survive this
            // guard or they are stuck on the win message.
            const isResetKey = event.code === 'NumpadAdd' || event.key === '+' || event.keyCode === 107 || event.which === 107;
            const isUndoKey = event.code === 'NumpadDecimal' || event.code === 'Numpad8' || event.key === '8' || event.keyCode === 110 || event.which === 110;
            if (isGameFinished && !isResetKey && !isUndoKey) return;
            // Dispatched here rather than from the numpad tables below so that the
            // main-row '+' reaches the reset too. It arrives as code 'Equal' with
            // keyCode 187, matches none of the Numpad* branches, and used to fall
            // through to the no-op default - leaving anyone on a keyboard without a
            // numpad with no way off the win message.
            if (isResetKey) {
                event.preventDefault();
                event.stopPropagation();
                actionFunctionsRef.current.resetFrames();
                return;
            }
            if (event.location === KeyboardEvent.DOM_KEY_LOCATION_NUMPAD) {
                event.preventDefault();
                event.stopPropagation();
                if (type === 'snooker') {
                    switch (event.code) {
                        case 'NumpadDivide': actionFunctionsRef.current.pauseTimer(); return;
                        case 'NumpadMultiply': actionFunctionsRef.current.resetTimer(); return;
                        case 'NumpadSubtract': actionFunctionsRef.current.winFrame(); return;
                        case 'NumpadAdd': actionFunctionsRef.current.resetFrames(); return;
                        case 'NumpadEnter': actionFunctionsRef.current.switchPlayer(); return;
                        case 'NumpadDecimal': actionFunctionsRef.current.undoAction(); return;
                        case 'Numpad1': actionFunctionsRef.current.potRed(); return;
                        case 'Numpad2': actionFunctionsRef.current.potColor('Yellow', 2); return;
                        case 'Numpad3': actionFunctionsRef.current.potColor('Green', 3); return;
                        case 'Numpad4': actionFunctionsRef.current.potColor('Brown', 4); return;
                        case 'Numpad5': actionFunctionsRef.current.potColor('Blue', 5); return;
                        case 'Numpad6': actionFunctionsRef.current.potColor('Pink', 6); return;
                        case 'Numpad7': actionFunctionsRef.current.potColor('Black', 7); return;
                        case 'Numpad8': actionFunctionsRef.current.undoAction(); return;
                        case 'Numpad9': actionFunctionsRef.current.winFrame(); return;
                    }
                } else {
                    switch (event.code) {
                        case 'Numpad1': actionFunctionsRef.current.startShotTimer(); return;
                        case 'Numpad2': actionFunctionsRef.current.stopShotTimer(); return;
                        case 'Numpad3': actionFunctionsRef.current.extendShotTimer(); return;
                        case 'NumpadDivide': actionFunctionsRef.current.pauseTimer(); return;
                        case 'NumpadMultiply': actionFunctionsRef.current.resetTimer(); return;
                        case 'NumpadSubtract': actionFunctionsRef.current.winFrame(); return;
                        case 'NumpadAdd': actionFunctionsRef.current.resetFrames(); return;
                        case 'NumpadEnter': actionFunctionsRef.current.switchPlayer(); return;
                        case 'NumpadDecimal': actionFunctionsRef.current.undoAction(); return;
                        case 'Numpad4':
                        case 'Numpad6': actionFunctionsRef.current.switchPlayer(); return;
                        case 'Numpad5': if (type === 'chinese-pool') actionFunctionsRef.current.addIllegalBreak(); return;
                    }
                }
            }
            if (event.code && event.code.startsWith('Numpad')) {
                event.preventDefault();
                event.stopPropagation();
                switch (event.code) {
                    case 'NumpadDivide': actionFunctionsRef.current.pauseTimer(); return;
                    case 'NumpadMultiply': actionFunctionsRef.current.resetTimer(); return;
                    case 'NumpadSubtract': actionFunctionsRef.current.winFrame(); return;
                    case 'NumpadAdd': actionFunctionsRef.current.resetFrames(); return;
                    case 'NumpadEnter': actionFunctionsRef.current.switchPlayer(); return;
                    case 'NumpadDecimal': actionFunctionsRef.current.undoAction(); return;
                    case 'Numpad1':
                        if (type !== 'snooker') { actionFunctionsRef.current.startShotTimer(); return; }
                        actionFunctionsRef.current.potRed(); return;
                    case 'Numpad2':
                        if (type !== 'snooker') { actionFunctionsRef.current.stopShotTimer(); return; }
                        actionFunctionsRef.current.potColor('Yellow', 2); return;
                    case 'Numpad3':
                        if (type !== 'snooker') { actionFunctionsRef.current.extendShotTimer(); return; }
                        actionFunctionsRef.current.potColor('Green', 3); return;
                    case 'Numpad4':
                        if (type !== 'snooker') { actionFunctionsRef.current.switchPlayer(); return; }
                        actionFunctionsRef.current.potColor('Brown', 4); return;
                    case 'Numpad5':
                        if (type === 'snooker') actionFunctionsRef.current.potColor('Blue', 5);
                        if (type === 'chinese-pool') actionFunctionsRef.current.addIllegalBreak();
                        return;
                    case 'Numpad6':
                        if (type !== 'snooker') { actionFunctionsRef.current.switchPlayer(); return; }
                        if (type === 'snooker') actionFunctionsRef.current.potColor('Pink', 6);
                        return;
                    case 'Numpad7':
                        if (type === 'snooker') actionFunctionsRef.current.potColor('Black', 7);
                        return;
                    case 'Numpad8': actionFunctionsRef.current.undoAction(); return;
                    case 'Numpad9': actionFunctionsRef.current.winFrame(); return;
                    case 'Numpad0': return;
                }
            }
            if (event.code === 'NumLock') {
                actionFunctionsRef.current.startTimer();
                return;
            }
            const keyCode = typeof event.keyCode === 'number' ? event.keyCode : (typeof event.which === 'number' ? event.which : undefined);
            if (typeof keyCode === 'number') {
                switch (keyCode) {
                    case 111: actionFunctionsRef.current.pauseTimer(); return;
                    case 106: actionFunctionsRef.current.resetTimer(); return;
                    case 109: actionFunctionsRef.current.winFrame(); return;
                    case 107: actionFunctionsRef.current.resetFrames(); return;
                    case 110: actionFunctionsRef.current.undoAction(); return;
                    case 13: actionFunctionsRef.current.switchPlayer(); return;
                    case 37:
                    case 39: actionFunctionsRef.current.switchPlayer(); return;
                }
                if (type === 'snooker' && keyCode >= 97 && keyCode <= 105) {
                    const num = keyCode - 96;
                    if (num === 1) actionFunctionsRef.current.potRed();
                    else if (num === 2) actionFunctionsRef.current.potColor('Yellow', 2);
                    else if (num === 3) actionFunctionsRef.current.potColor('Green', 3);
                    else if (num === 4) actionFunctionsRef.current.potColor('Brown', 4);
                    else if (num === 5) actionFunctionsRef.current.potColor('Blue', 5);
                    else if (num === 6) actionFunctionsRef.current.potColor('Pink', 6);
                    else if (num === 7) actionFunctionsRef.current.potColor('Black', 7);
                    else if (num === 8) actionFunctionsRef.current.undoAction();
                    else if (num === 9) actionFunctionsRef.current.winFrame();
                    return;
                }
                if (type === 'snooker' && keyCode >= 48 && keyCode <= 57) {
                    const num = keyCode - 48;
                    if (num === 0) {
                        actionFunctionsRef.current.switchPlayer();
                    }
                    else if (num === 1) actionFunctionsRef.current.potRed();
                    else if (num === 2) actionFunctionsRef.current.potColor('Yellow', 2);
                    else if (num === 3) actionFunctionsRef.current.potColor('Green', 3);
                    else if (num === 4) actionFunctionsRef.current.potColor('Brown', 4);
                    else if (num === 5) actionFunctionsRef.current.potColor('Blue', 5);
                    else if (num === 6) actionFunctionsRef.current.potColor('Pink', 6);
                    else if (num === 7) actionFunctionsRef.current.potColor('Black', 7);
                    else if (num === 8) actionFunctionsRef.current.undoAction();
                    else if (num === 9) actionFunctionsRef.current.winFrame();
                    return;
                }
            }
            switch (event.key) {
                case 'Enter':
                    if (event.code !== 'NumpadEnter') actionFunctionsRef.current.switchPlayer();
                    break;
                case 'ArrowLeft':
                case 'ArrowRight':
                    actionFunctionsRef.current.switchPlayer();
                    break;
                case '1':
                    if (type === 'snooker' && (event.code === 'Digit1' || !event.code || event.code === 'Unknown')) actionFunctionsRef.current.potRed();
                    break;
                case '2':
                    if (type === 'snooker' && (event.code === 'Digit2' || !event.code || event.code === 'Unknown')) actionFunctionsRef.current.potColor('Yellow', 2);
                    break;
                case '3':
                    if (type === 'snooker' && (event.code === 'Digit3' || !event.code || event.code === 'Unknown')) actionFunctionsRef.current.potColor('Green', 3);
                    break;
                case '4':
                    if (type === 'snooker' && (event.code === 'Digit4' || !event.code || event.code === 'Unknown')) actionFunctionsRef.current.potColor('Brown', 4);
                    break;
                case '5':
                    if (type === 'snooker' && (event.code === 'Digit5' || !event.code || event.code === 'Unknown')) actionFunctionsRef.current.potColor('Blue', 5);
                    break;
                case '6':
                    if (type === 'snooker' && (event.code === 'Digit6' || !event.code || event.code === 'Unknown')) actionFunctionsRef.current.potColor('Pink', 6);
                    break;
                case '7':
                    if (type === 'snooker' && (event.code === 'Digit7' || !event.code || event.code === 'Unknown')) actionFunctionsRef.current.potColor('Black', 7);
                    break;
                case '8':
                    if (type === 'snooker' && (event.code === 'Digit8' || !event.code || event.code === 'Unknown')) actionFunctionsRef.current.undoAction();
                    break;
                case '9':
                    if (type === 'snooker' && (event.code === 'Digit9' || !event.code || event.code === 'Unknown')) actionFunctionsRef.current.winFrame();
                    break;
                case '0':
                    if (event.code === 'Digit0' || !event.code || event.code === 'Unknown') actionFunctionsRef.current.switchPlayer();
                    break;
                default: break;
            }
        } catch (err) {
            try { console.error('Key handler error', err); } catch (_) { }
        }
    }, [isGameFinished, type, view]);

    useEffect(() => {
        const confirmOnReferee = (action, handler) => () => {
            if (type === 'snooker' && view === 'referee') refereeConfirmationRef.current?.(action);
            else handler();
        };
        actionFunctionsRef.current = { potRed, potColor, switchPlayer,
            winFrame: confirmOnReferee('winFrame', winFrame),
            resetFrames: confirmOnReferee('resetFrames', resetFrames),
            resetTimer: confirmOnReferee('resetTimer', resetTimer),
            undoAction, startShotTimer, stopShotTimer, extendShotTimer, addIllegalBreak, startTimer, pauseTimer, freeBall, addFoul, cancelScoringMode };
    }, [potRed, potColor, switchPlayer, winFrame, resetFrames, undoAction, startShotTimer, stopShotTimer, extendShotTimer, addIllegalBreak, startTimer, pauseTimer, resetTimer, freeBall, addFoul, cancelScoringMode, type, view]);
    useEffect(() => {
        if (type !== 'snooker') return;
        const intervalId = setInterval(() => {
            if (gameState.timerIsRunning || gameState.shotTimerIsRunning) {
                updateDisplayTime();
            }
            if (!gameState.frameExpired && getShotTimerEnabled()) {
                const frameElapsed = calculateCurrentTime();
                if (frameElapsed >= 600000) {
                    setGameState(prevState => {
                        if (prevState.frameExpired) return prevState;
                        pauseTimer();
                        setDisplayTime('00:10:00');
                        const newState = {
                            ...prevState,
                            frameExpired: true,
                            sequence: prevState.sequence + 1,
                            timestamp: Date.now()
                        };
                        emitGameState(newState, false);
                        return newState;
                    });
                }
            }
            if (gameState.shotTimerIsRunning) {
                const remaining = calculateShotRemaining();
                const currentSecond = Math.floor(remaining / 1000);
                if (remaining <= 0) {
                    setGameState(prevState => {
                        const frameElapsed = calculateCurrentTime();
                        const newLimitMs = frameElapsed >= 300000 ? 10000 : 15000;
                        const newState = {
                            ...prevState,
                            shotTimerIsRunning: false,
                            shotStartTimestamp: null,
                            shotRemaining: newLimitMs,
                            currentShotLimitMs: newLimitMs,
                            sequence: prevState.sequence + 1,
                            timestamp: Date.now(),
                            timerOwner: prevState.timerOwner === 1 ? 2 : 1,
                        };
                        emitGameState(newState, false);
                        return newState;
                    });
                    enqueueAction(switchPlayer);
                    lastEmittedSecondRef.current = null;
                } else if (lastEmittedSecondRef.current !== currentSecond) {
                    lastEmittedSecondRef.current = currentSecond;
                }
            }
        }, 250);
        return () => {
            clearInterval(intervalId);
            lastEmittedSecondRef.current = null;
        };
    }, [type, gameState.timerIsRunning, gameState.shotTimerIsRunning, gameState.frameExpired, updateDisplayTime, calculateCurrentTime, calculateShotRemaining, emitGameState, getShotTimerEnabled, socket]); // Stable dependencies
    useEffect(() => {
        if (!loading && user && isAuthenticated()) {
            checkGameOwnership();
        } else if (!loading) {
            setGameLoading(false);
        }
    }, [user, loading, isAuthenticated, linkId]);
    useEffect(() => {
        const init = async () => {
            if (user || isPublic) {
                await getGame();
            }
        };
        init();
    }, [user, linkId, type]);
    useEffect(() => {
        if (game) {
            let p1Name = game.first_player_name;
            let p2Name = game.second_player_name;
            let p1Nat = game.first_player_nationality;
            let p2Nat = game.second_player_nationality;
            if (players.length > 0) {
                const p1 = players.find(p => String(p.id) === String(game.first_player));
                const p2 = players.find(p => String(p.id) === String(game.second_player));
                if (p1) {
                    if (!p1Name) p1Name = p1.name;
                    if (!p1Nat) p1Nat = p1.nationality;
                }
                if (p2) {
                    if (!p2Name) p2Name = p2.name;
                    if (!p2Nat) p2Nat = p2.nationality;
                }
            }
            setGameState(prevState => {
                if (prevState.firstPlayer === p1Name && prevState.secondPlayer === p2Name && prevState.firstPlayerNationality === p1Nat && prevState.secondPlayerNationality === p2Nat) {
                    return prevState;
                }
                return {
                    ...prevState,
                    firstPlayer: p1Name || prevState.firstPlayer,
                    secondPlayer: p2Name || prevState.secondPlayer,
                    firstPlayerNationality: p1Nat || prevState.firstPlayerNationality,
                    secondPlayerNationality: p2Nat || prevState.secondPlayerNationality,
                };
            });
        }
    }, [game, players]);
    useEffect(() => {
        hasReceivedInitialDataRef.current = hasReceivedInitialData;
        if (hasReceivedInitialData) {
            fetchAndApplySettings();
        }
    }, [hasReceivedInitialData]);
    useEffect(() => {
        if (type !== 'snooker') return;
        if (memoizedMaxPoints !== gameState.maxPoints) {
            setGameState(prevState => {
                const newState = {
                    ...prevState,
                    maxPoints: memoizedMaxPoints,
                    sequence: prevState.sequence + 1,
                    timestamp: Date.now()
                };
                emitGameState(newState, true);
                return newState;
            });
        }
    }, [memoizedMaxPoints, type, gameState.maxPoints, emitGameState]);
    useEffect(() => {
        isInitializingRef.current = isInitializing;
    }, [isInitializing]);
    useEffect(() => {
        const handleOnline = () => {
            apiService.processOfflineQueue();
            if (socket && !socket.connected) {
                socket.connect();
            }
        };
        const handleOffline = () => {
            if (socket && socket.connected) {
                socket.disconnect();
            }
        };
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [socket]);
    useEffect(() => {
        if (game?.id) {
            const socketUrl = config.SOCKET_URL;
            const newSocket = io(socketUrl, {
                transports: ['polling', 'websocket'],
                timeout: 20000,
                forceNew: true,
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000
            });
            setSocket(newSocket);
            let stateRequestTimeout;
            newSocket.on('connect', () => {
                newSocket.emit('joinGame', game.id);
                // Also join the table room so we get notified when match is linked/unlinked/deleted
                if (game.id_table) {
                    newSocket.emit('joinTableRoom', game.id_table);
                }
                if (hasReceivedInitialDataRef.current) {
                    newSocket.emit('gameStateUpdate', getCurrentGameStateRef.current());
                    apiService.processOfflineQueue();
                }
                stateRequestTimeout = setTimeout(() => {
                    setHasReceivedInitialData(true);
                    setIsInitializing(false);
                }, 1000);
            });
            newSocket.on('initialGameState', (incomingState) => {
                if (stateRequestTimeout) {
                    clearTimeout(stateRequestTimeout);
                    stateRequestTimeout = null;
                }

                if (incomingState && incomingState.id === game.id) {
                    setHasReceivedInitialData(true);
                    setIsInitializing(false);
                    syncStateFromSocket(incomingState);
                } else {
                    setHasReceivedInitialData(true);
                    setIsInitializing(false);
                }
            });
            newSocket.on('requestCurrentState', (requestedGameId) => {
                if ((requestedGameId == null || String(requestedGameId) === String(game.id)) && hasReceivedInitialDataRef.current && !isInitializingRef.current) {
                    const currentState = getCurrentGameStateRef.current();
                    newSocket.emit('provideCurrentState', currentState);
                }
            });
            newSocket.on('gameStateUpdate', (incomingState) => {
                if (incomingState.id === game.id) {
                    if (stateRequestTimeout) {
                        clearTimeout(stateRequestTimeout);
                        stateRequestTimeout = null;
                    }

                    syncStateFromSocket(incomingState);
                }
            });
            newSocket.on('provideCurrentState', (incomingState) => {
                if (incomingState && incomingState.id === game.id) {
                    if (stateRequestTimeout) {
                        clearTimeout(stateRequestTimeout);
                        stateRequestTimeout = null;
                    }
                    setHasReceivedInitialData(true);
                    setIsInitializing(false);
                    syncStateFromSocket(incomingState);
                }
            });
            newSocket.on('refreshGame', (gameId) => {
                if (gameId == game.id) {
                    try { window.location.reload(); } catch (e) {}
                }
            });
            // When a match is linked/unlinked/deleted on this table, reload
            // to pick up the correct game. This is the same event the TV uses.
            newSocket.on('tableGameLinked', () => {
                console.log('[GameWrapper] tableGameLinked — reloading page');
                try { window.location.reload(); } catch (e) {}
            });
            return () => {
                if (stateRequestTimeout) {
                    clearTimeout(stateRequestTimeout);
                }
                newSocket.emit('leaveGame', game.id);
                newSocket.disconnect();
            };
        }
    }, [game?.id, config.SOCKET_URL]);
    useEffect(() => {
        if (game) {
            if (game.status === 'finished') {
                setIsGameFinished(true);
                setWinMessage(`Game has been finished`);
                setGameState(prevState => {
                    const newState = {
                        ...prevState,
                        isGameFinished: true,
                        winMessage: `Game has been finished`,
                        sequence: prevState.sequence + 1,
                        timestamp: Date.now()
                    };
                    emitGameState(newState, true);
                    return newState;
                });
            } else {
                const isRace = game.is_race == 1 || game.is_race === '1';
                const raceTo = parseInt(game.race_to);
                if (isRace && raceTo) {
                    if (gameState.player1FramesWon >= raceTo) {
                        setGameWinner(game.first_player);
                        setIsGameFinished(true);
                        setWinMessage(`${game.first_player_name} wins the match! Waiting for next match...`);
                        setGameState(prevState => {
                            const newState = {
                                ...prevState,
                                isGameFinished: true,
                                winMessage: `${game.first_player_name} wins the match! Waiting for next match...`,
                                sequence: prevState.sequence + 1,
                                timestamp: Date.now()
                            };
                            emitGameState(newState, true);
                            return newState;
                        });
                    } else if (gameState.player2FramesWon >= raceTo) {
                        setGameWinner(game.second_player);
                        setIsGameFinished(true);
                        setWinMessage(`${game.second_player_name} wins the match! Waiting for next match...`);
                        setGameState(prevState => {
                            const newState = {
                                ...prevState,
                                isGameFinished: true,
                                winMessage: `${game.second_player_name} wins the match! Waiting for next match...`,
                                sequence: prevState.sequence + 1,
                                timestamp: Date.now()
                            };
                            emitGameState(newState, true);
                            return newState;
                        });
                    } else {
                        setIsGameFinished(false);
                        setWinMessage(null);
                        setGameState(prevState => {
                            const newState = {
                                ...prevState,
                                isGameFinished: false,
                                sequence: prevState.sequence + 1,
                                timestamp: Date.now()
                            };
                            emitGameState(newState, true);
                            return newState;
                        });
                    }
                } else {
                    setIsGameFinished(false);
                    setWinMessage(null);
                    setGameState(prevState => {
                        const newState = {
                            ...prevState,
                            isGameFinished: false,
                            sequence: prevState.sequence + 1,
                            timestamp: Date.now()
                        };
                        emitGameState(newState, true);
                        return newState;
                    });
                }
            }
        }
    }, [gameState.player1FramesWon, gameState.player2FramesWon, game, socket]);
    useEffect(() => {
        if (actionStack.length > 15) {
            let newActionStack = [...actionStack];
            newActionStack.shift();
            setActionStack(newActionStack);
        }
    }, [actionStack]);
    useEffect(() => {
        document.addEventListener('keydown', keyDownFunction, true);
        return () => {
            document.removeEventListener('keydown', keyDownFunction, true);
        };
    }, [keyDownFunction]);
    useEffect(() => {
        if (gameState.isGameFinished && shotTimer) {
            clearInterval(shotTimer);
            setShotTimer(null);
        }
    }, [gameState.isGameFinished]);
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            if (frameTimer !== null) {
                clearInterval(frameTimer);
            }
            if (shotTimer) {
                clearInterval(shotTimer);
            }
        };
    }, []);

    if (!['snooker', 'pool', 'us-pool', 'chinese-pool'].includes(type)) {
        return <Navigate to="/" replace />;
    }
    if (!['scoreboard', 'referee', 'referee-shootout', 'display-global-timer', 'display-shot-timer', 'display-shootout-info'].includes(view)) {
        return <Navigate to="/" replace />;
    }
    if (view === 'display-global-timer') {
        return <ShootoutGlobalTimer calculateCurrentTime={calculateDisplayTime} formatTime={formatTime} />;
    }
    if (view === 'display-shot-timer') {
        return (
            <ShootoutShotTimer
                calculateShotRemaining={calculateShotRemaining}
                gameState={gameState}
                calculateCurrentTime={calculateCurrentTime}
            />
        );
    }
    if (view === 'display-shootout-info') {
        return (
            <ShootoutInfoBoard
                gameState={gameState}
                game={game}
                calculateDisplayTime={calculateDisplayTime}
                calculateShotRemaining={calculateShotRemaining}
                formatTime={formatTime}
            />
        );
    }
    if (loading || gameLoading) {
        return (
            <div className="page-wrapper">
                <div className="page-body">
                    <div className="container-xl w-100">
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    if (!isPublic && (!isAuthenticated() || !user)) {
        return <Navigate to="/login" replace />;
    }
    if (!isPublic && gameOwnership === false) {
        return <Navigate to="/" replace />;
    }
    const props = {
        gameState,
        scoreboardTheme,
        setGameState,
        settings,
        displayTime,
        frameBreaks,
        setFrameBreaks,
        actionStack,
        setActionStack,
        game,
        isLinkedGame,
        isGameFinished,
        setIsGameFinished,
        players,
        socket,
        showControls,
        setShowControls,
        lastAcceptedSequenceRef,
        hasReceivedInitialData,
        isInitializing,
        lastLocalUpdate,
        frameTimer,
        setFrameTimer,
        shotTimer,
        setShotTimer,
        loadingSettings,
        calculateCurrentTime,
        formatTime,
        updateDisplayTime,
        getCurrentGameState: getCurrentGameStateRef.current,
        syncStateFromSocket,
        markLocalAction,
        getFlagComponent,
        formatShotTime,
        user,
        linkId,
        type,
        potRed,
        potColor,
        switchPlayer,
        startShotTimer,
        stopShotTimer,
        extendShotTimer,
        startTimer,
        pauseTimer,
        resetTimer,
        freeBall,
        addFoul,
        addFoulPoints: addPoints,
        potFreeBall,
        adjustReds,
        cancelScoringMode,
        forceSync,
        controlStreamingStats,
        refereeConfirmationRef,
        winFrame,
        resetFrames,
        addIllegalBreak,
        undoAction,
        toggleFullScreen,
        calculateShotRemaining,
        resetShotTimer,
        winMessage
    };
    if (view === 'referee') {
        return (
            <>
                <RefereeControls {...props} />
                {type === 'snooker' && null}
            </>
        );
    }
    if (view === 'referee-shootout') {
        return (
            <RefereeShootout
                gameState={gameState}
                startShotTimer={startShotTimer}
                stopShotTimer={stopShotTimer}
                resetShotTimer={resetShotTimer}
                startTimer={startTimer}
                pauseTimer={pauseTimer}
                resetTimer={resetTimer}
                toggleTimerOwner={toggleTimerOwner}
                toggleTimerSync={toggleTimerSync}
                toggleTimerSound={toggleTimerSound} formatShotTime={formatShotTime}
                formatTime={formatTime}
                calculateShotRemaining={calculateShotRemaining}
                calculateCurrentTime={calculateCurrentTime}
            />
        );
    }
    switch (type) {
        case 'snooker':
            return <Snooker {...props} />;
        case 'pool':
            return <Pool {...props} />;
        case 'us-pool':
            return <UsPool {...props} />;
        case 'chinese-pool':
            return <ChinesePool {...props} />;
        default:
            return <Navigate to="/" replace />;
    }
};
export default GameWrapper;
